import React, { useState, useEffect, useCallback, useRef } from "react";
import useDarkMode from '../../hooks/useDarkMode';
import useThemeColor from "../../hooks/useThemeColor";
import { useSelector } from "react-redux";
import { useNavigate, useParams, useLocation, Link } from "react-router-dom";
import axiosInstance from "../../config/axiosApi/axiosApi";
import APIs from "../../Constants/apis";
import GridSectionLoader from "../../components/CustomComponents/preLoad/gridSectionLoader";
import showErrorToast, {
  showSuccessToast,
} from "../../components/CustomComponents/toast/toast";
import FormInput from "../../components/CustomComponents/Input/formInput";
import ToggleSwitch from "../../components/CustomComponents/Switchery/switchery";
import "../../components/DateTimePicker/dateTimePicker.css";
import "../../components/DateTimePicker/materialUIOverrides.css";
import moment from "moment";
import ScheduleManager from "../../components/CustomComponents/TimeSchedule/weeklySlots";
import schedule from "../../components/CustomComponents/TimeSchedule/schedule";
import CommonTimeSlotForAllWeek from "../../components/CustomComponents/TimeSchedule/commonTimeSlotForAllWeek";
import {
  checkIfCommonTimeSlots,
  convertToScheduleFormat,
} from "../../components/CustomComponents/TimeSchedule/scheduleConv";
import ProductMultiSelectionModal from "../../components/CustomComponents/modal/productMultiSelectionModel";
import CategorySelector from "../../components/CustomComponents/modal/CategorySelector";
import DealSlotTypeSelectionModal from "../../components/CustomComponents/modal/DealSlotTypeSelectionModal";
import Select from "react-select";
import DropDownStyles, { DropDownStylesDark } from "../staff/dropdownStyles";
import swal from "sweetalert";
import DealFormBasics from "./DealFormBasics";
import { DealSection, DealToggleRow } from "./dealFormUI";
import "./dealForm.css";
import useUnsavedChangesGuard from "../../hooks/useUnsavedChangesGuard";

const UpdateDeal = () => {
  const { dealId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const CompID = useSelector((state) => state.auth.user)?.userData?.compID;
  const { profileData } = useSelector((state) => state.auth);
  const [compCurrency, setCompCurrency] = useState("GBP");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [modifiedBy, setModifiedBy] = useState("");
  const [categoryModalLoading, setCategoryModalLoading] = useState(false);

  const themeColor = useThemeColor();
  const isDarkMode = useDarkMode();
  const dropdownStyles = isDarkMode ? DropDownStylesDark : DropDownStyles;

  // Variant options removed - users can now type any variant name

  const [formState, setFormState] = useState({
    dealID: 0,
    dealName: "",
    dealDescription: "",
    dealPrice: "",
    dealOrder: "0",
    minimumOrderValue: "",
    dealPricingType: "CustomPrice",
    dealDiscountPercentage: "",
    startDate: null,
    endDate: null,
  });

  const [toggles, setToggles] = useState({
    isActive: true,
    showInKiosk: true,
    showInPOS: true,
    showInOnline: true,
    autoDuplicateItem: false,
    isScheduleDeal: false,
  });

  const [imageState, setImageState] = useState({
    dealImage: "",
    imageFile: null,
  });

  const [slots, setSlots] = useState([]);
  const [expandedSlots, setExpandedSlots] = useState(new Set());
  const [initialLoadedDataSnapshot, setInitialLoadedDataSnapshot] = useState(null);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const selectedSlotRef = useRef(null); // Ref to preserve selectedSlot during async operations
  const selectedSlotIndexRef = useRef(null); // Ref to preserve selectedSlot index during async operations
  const typeSelectionInProgressRef = useRef(false); // Ref to track if type selection is in progress
  const formStateRef = useRef(formState); // Ref to always have latest formState
  const [showTypeSelectionModal, setShowTypeSelectionModal] = useState(false);
  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [showItemModal, setShowItemModal] = useState(false);
  const [selectedType, setSelectedType] = useState(null); // 'categories' or 'items'
  const [allCategories, setAllCategories] = useState([]);
  const [allItems, setAllItems] = useState([]);
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [selectedItems, setSelectedItems] = useState([]);
  const [slotSaving, setSlotSaving] = useState(false);
  const initialTab =
    location.state?.activeTab === "schedule" || location.state?.activeTab === "slots"
      ? location.state.activeTab
      : "basics";
  const [activeTab, setActiveTab] = useState(initialTab);

  // Schedule state
  const [useSameTimeSlots, setUseSameTimeSlots] = useState(true);
  const [schedules, setSchedules] = useState(schedule);
  const [commonTimeSlot, setCommonTimeSlot] = useState([
    { from: "00:00", to: "23:59" },
  ]);

  const currencySymbols = {
    GBP: "£",
    USD: "$",
    EUR: "€",
  };

  const getCurrencySymbol = (currencyCode) => {
    return currencySymbols[currencyCode] || currencyCode;
  };

  const applyCommonTimeSlotToAll = () => {
    const updatedSchedules = schedules.map((schedule) => ({
      ...schedule,
      timeSlots: commonTimeSlot.map((slot) => ({ ...slot })),
    }));
    setSchedules(updatedSchedules);
  };

  useEffect(() => {
    if (profileData) {
      setCompCurrency(profileData?.compCurrency || "GBP");
      setModifiedBy(profileData?.userName || "");
    }
  }, [profileData]);

  // Fetch all categories and items for selection
  const fetchCategories = useCallback(async () => {
    if (!CompID) return;
    try {
      const response = await axiosInstance.get(APIs.GET_CATEGORIES, {
        params: {
          CompID,
          SortBy: 'rootCat_Orderby',
          SortOrder: 'ASC',
          Search: '',
        }
      });
      const categoryData = response.data?.data || response.data;
      if (Array.isArray(categoryData)) {
        setAllCategories(categoryData);
      }
    } catch (error) {
      console.error("Error fetching categories:", error);
    }
  }, [CompID]);

  const fetchItems = useCallback(async () => {
    try {
      const response = await axiosInstance.get(APIs.GET_ALL_ONLY_PRODUCTS, {
        params: { CompID }
      });
      if (response.data.status && response.data.data) {
        setAllItems(response.data.data);
      }
    } catch (error) {
      console.error("Error fetching items:", error);
    }
  }, [CompID]);

  useEffect(() => {
    fetchCategories();
    fetchItems();
  }, [fetchCategories, fetchItems]);

  // Keep ref in sync with selectedSlot state
  useEffect(() => {
    selectedSlotRef.current = selectedSlot;
  }, [selectedSlot]);

  // Keep formStateRef in sync with formState
  useEffect(() => {
    formStateRef.current = formState;
  }, [formState]);

  const getDealSnapshot = useCallback(() => {
    const start =
      formState.startDate && typeof formState.startDate.toISOString === "function"
        ? formState.startDate.toISOString()
        : formState.startDate
          ? String(formState.startDate)
          : null;
    const end =
      formState.endDate && typeof formState.endDate.toISOString === "function"
        ? formState.endDate.toISOString()
        : formState.endDate
          ? String(formState.endDate)
          : null;
    return JSON.stringify({
      formState: {
        ...formState,
        startDate: start,
        endDate: end,
      },
      toggles,
      dealImage: imageState.dealImage || "",
      imageFileName: imageState.imageFile?.name || "",
      slots: (slots || []).map((s) => ({
        slotID: s.slotID || 0,
        slotName: s.slotName || "",
        requiredQty: s.requiredQty || 0,
        slotOrder: s.slotOrder || 0,
        _isUnsaved: Boolean(s._isUnsaved),
        allowedCategories: (s.allowedCategories || []).map((c) => c.categoryID || c.id),
        allowedItems: (s.allowedItems || []).map((i) => i.menuItem_ID || i.menuItemID || i.id),
        pricingType: s.pricingType || null,
        discountPercentage: s.discountPercentage ?? null,
        isFree: Boolean(s.isFree),
        customPrice: s.customPrice ?? null,
      })),
      useSameTimeSlots: Boolean(useSameTimeSlots),
      commonTimeSlot,
      schedules,
    });
  }, [formState, toggles, imageState.dealImage, imageState.imageFile, slots, useSameTimeSlots, commonTimeSlot, schedules]);

  // Wait for post-load schedule/slot hydration to settle before locking baseline.
  // Snapshotting immediately when loading flips false falsely marks the form dirty.
  const getDealSnapshotRef = useRef(() => "");
  useEffect(() => {
    getDealSnapshotRef.current = getDealSnapshot;
  }, [getDealSnapshot]);

  const [baselineReady, setBaselineReady] = useState(false);

  useEffect(() => {
    if (loading || !formState.dealID) {
      setBaselineReady(false);
      setInitialLoadedDataSnapshot(null);
      return undefined;
    }
    if (baselineReady) return undefined;

    let cancelled = false;
    const timer = window.setTimeout(() => {
      if (cancelled) return;
      setInitialLoadedDataSnapshot(getDealSnapshotRef.current());
      setBaselineReady(true);
    }, 100);

    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [loading, formState.dealID, getDealSnapshot, baselineReady]);

  const isDirty =
    baselineReady &&
    initialLoadedDataSnapshot !== null &&
    getDealSnapshot() !== initialLoadedDataSnapshot;

  const fetchDealData = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get(APIs.GET_DEAL_BY_ID, {
        params: {
          DealID: dealId,
          CompID,
        },
      });
      console.log("Deal response:", response.data);

      if (response.data.status && response.data.data) {
        const deal = response.data.data;
        const pricingType = (deal.pricingType || deal.PricingType || "CustomPrice").trim();
        // Only show dealPrice when pricing type is CustomPrice; only show discount when Discount (ignore stale DB values for the other type)
        const newFormState = {
          dealID: deal.dealID,
          dealName: deal.dealName || "",
          dealDescription: deal.dealDescription || "",
          dealPrice: pricingType === "CustomPrice" ? (deal.dealPrice != null && deal.dealPrice !== "" ? String(deal.dealPrice) : "") : "",
          dealOrder: deal.dealOrder?.toString() || "0",
          minimumOrderValue: deal.minimumOrderValue?.toString() || "",
          dealPricingType: pricingType,
          dealDiscountPercentage: pricingType === "Discount" ? (deal.discountPercentage != null ? String(deal.discountPercentage) : (deal.DiscountPercentage != null ? String(deal.DiscountPercentage) : "")) : "",
          startDate: deal.startDate ? moment(deal.startDate) : null,
          endDate: deal.endDate ? moment(deal.endDate) : null,
        };
        setFormState(newFormState);
        formStateRef.current = newFormState; // Update ref when fetching deal data

        setToggles({
          isActive: deal.isActive !== false,
          showInKiosk: deal.showInKiosk !== false,
          showInPOS: deal.showInPOS !== false,
          showInOnline: deal.showInOnline !== false,
          autoDuplicateItem: deal.autoDuplicateItem === true,
          isScheduleDeal: deal.isScheduleDeal === true,
        });

        // Handle weekly timings if schedule is enabled
        if (deal.isScheduleDeal === true && deal.dealWeeklyTimings && deal.dealWeeklyTimings.length > 0) {
          try {
            console.log("Processing deal weekly timings:", deal.dealWeeklyTimings);
            
            const weeklyTimings = deal.dealWeeklyTimings;
            
            // Check if all days have the same time slots
            const isCommon = checkIfCommonTimeSlots(weeklyTimings);
            console.log("Are time slots common across all days?", isCommon);
            
            if (isCommon && weeklyTimings.length > 0) {
              // All days have same time slots - use "same for all days" mode
              // The CommonTimeSlotForAllWeek component expects times in HH:mm format (24-hour)
              const firstDaySlots = weeklyTimings
                .filter((day) => day.dayName === weeklyTimings[0].dayName)
                .map((day) => {
                  console.log(`Time slot: ${day.openTime} to ${day.closeTime}`);
                  return {
                    from: day.openTime,  // Keep as HH:mm (24-hour format)
                    to: day.closeTime,   // Keep as HH:mm (24-hour format)
                  };
                });
              console.log("Common time slots (HH:mm format):", firstDaySlots);
              setCommonTimeSlot(firstDaySlots);
              setUseSameTimeSlots(true);
            } else {
              // Different time slots per day
              console.log("Using different time slots for each day");
              setUseSameTimeSlots(false);
            }
            
            // Convert and set schedules for individual day view
            // convertToScheduleFormat expects times in HH:mm format (24-hour)
            const convertedSchedules = convertToScheduleFormat(weeklyTimings);
            console.log("Converted schedules:", convertedSchedules);
            setSchedules(convertedSchedules);
          } catch (error) {
            console.error("Error processing deal weekly timings:", error);
            // Reset to defaults on error
            setSchedules(schedule);
            setCommonTimeSlot([{ from: "00:00", to: "23:59" }]);
            setUseSameTimeSlots(true);
          }
        } else {
          console.log("No schedule enabled or no weekly timings data");
          setSchedules(schedule);
          setCommonTimeSlot([{ from: "00:00", to: "23:59" }]);
          setUseSameTimeSlots(true);
        }

        // Construct full URL for image display if it's a relative path
        const baseURL = axiosInstance.defaults.baseURL || "";
        let imageDisplayUrl = deal.dealImage || "";
        if (imageDisplayUrl && !imageDisplayUrl.startsWith("http")) {
          // If it's a relative path, construct full URL for display
          const baseUrlClean = baseURL.endsWith("/") ? baseURL.slice(0, -1) : baseURL;
          const imagePath = imageDisplayUrl.startsWith("/") ? imageDisplayUrl : `/${imageDisplayUrl}`;
          imageDisplayUrl = `${baseUrlClean}${imagePath}`;
        }
        
        setImageState({
          dealImage: imageDisplayUrl,
          imageFile: null,
        });

        // Fetch slots with all related data (pass deal pricing type so slots default to NoDiscount when not SlotPricing)
        await fetchSlots(deal.dealID, null, deal.pricingType || deal.PricingType);
      } else {
        showErrorToast("Deal not found");
        navigate("/deal/manageDeals");
      }
    } catch (error) {
      console.error("Error fetching deal:", error);
      showErrorToast("Failed to load deal");
      navigate("/deal/manageDeals");
    } finally {
      setLoading(false);
    }
  }, [dealId, CompID, navigate]);

  const fetchSlots = useCallback(async (dealID, keepExpandedSlotID = null, dealPricingType = null) => {
    try {
      const response = await axiosInstance.get(APIs.GET_DEAL_SLOTS_BY_DEAL_ID, {
        params: { DealID: dealID },
      });
      
      const isSlotPricing = (dealPricingType || "").trim() === "SlotPricing";
      if (response.data.status && response.data.data) {
        const slotsData = await Promise.all(
          response.data.data.map(async (slot) => {
            // Fetch allowed categories
            const categoriesRes = await axiosInstance.get(
              APIs.GET_DEAL_SLOT_ALLOWED_CATEGORIES,
              { params: { SlotID: slot.slotID } }
            );
            const categories = categoriesRes.data.status ? categoriesRes.data.data : [];
            console.log(`Categories for slot ${slot.slotID}:`, categories);
            
            // Normalize category data (handle both PascalCase and camelCase from API).
            // Prefer CategoryName from SP join so chips don't fall back to "Category {id}".
            const normalizedCategories = Array.isArray(categories) ? categories.map(cat => ({
              id: cat.id || cat.ID,
              slotID: cat.slotID || cat.SlotID,
              categoryID: cat.categoryID || cat.CategoryID,
              categoryName:
                cat.categoryName ||
                cat.CategoryName ||
                cat.rootCat_Name ||
                cat.RootCat_Name ||
                null,
            })) : [];

            // Fetch allowed items
            const itemsRes = await axiosInstance.get(
              APIs.GET_DEAL_SLOT_ALLOWED_ITEMS,
              { params: { SlotID: slot.slotID } }
            );
            const items = itemsRes.data.status ? itemsRes.data.data : [];
            console.log(`Items for slot ${slot.slotID}:`, items);
            
            // Normalize item data (handle both PascalCase and camelCase from API)
            const normalizedItems = Array.isArray(items) ? items.map(item => ({
              id: item.id || item.ID,
              slotID: item.slotID || item.SlotID,
              // Robustly handle different API field names
              itemID: item.itemID || item.ItemID || item.menuItem_ID || item.MenuItem_ID || item.MenuItemID || item.MenuItemId || item.id || item.ID,
            })) : [];

            // Fetch constraints
            const constraintRes = await axiosInstance.get(
              APIs.GET_DEAL_SLOT_CONSTRAINT,
              { params: { SlotID: slot.slotID } }
            );
            const constraint = constraintRes.data.status ? constraintRes.data.data : null;

            // When deal pricing type is NOT SlotPricing, every slot is NoDiscount by default
            const slotPricingType = isSlotPricing ? (slot.pricingType || slot.PricingType || "NoDiscount") : "NoDiscount";
            return {
              ...slot,
              allowedCategories: normalizedCategories,
              allowedItems: normalizedItems,
              constraint: constraint,
              _isUnsaved: false,
              buyQuantity: slot.buyQuantity !== undefined && slot.buyQuantity !== null ? slot.buyQuantity : 0,
              pricingType: slotPricingType,
              discountPercentage: isSlotPricing ? (slot.discountPercentage ?? slot.DiscountPercentage ?? null) : null,
              isFree: isSlotPricing ? (slot.isFree || slot.IsFree || false) : false,
              customPrice: isSlotPricing ? (slot.customPrice ?? slot.CustomPrice ?? null) : null,
              autoDuplicateItem: slot.autoDuplicateItem ?? slot.AutoDuplicateItem ?? false,
              selectedVariant: (slot.selectedVariant || slot.SelectedVariant || "").toString().trim() || null,
            };
          })
        );
        console.log("All slots data after normalization:", slotsData);
        setSlots(slotsData);
        // When deleting a category/item, keep the same slot expanded; otherwise close all
        if (keepExpandedSlotID != null && keepExpandedSlotID > 0) {
          const indexToExpand = slotsData.findIndex(s => (s.slotID || s.SlotID) === keepExpandedSlotID);
          if (indexToExpand >= 0) {
            setExpandedSlots(new Set([indexToExpand]));
          } else {
            setExpandedSlots(new Set());
          }
        } else {
          setExpandedSlots(new Set());
        }
      }
    } catch (error) {
      console.error("Error fetching slots:", error);
      showErrorToast("Failed to refresh slot data");
    }
  }, []);

  useEffect(() => {
    if (dealId) {
      fetchDealData();
    }
  }, [dealId, fetchDealData]);

  const handleInputChange = (field, value) => {
    setFormState((prev) => ({
      ...prev,
      [field]: value,
    }));
    // When deal pricing type is changed to NOT SlotPricing, every slot defaults to NoDiscount
    if (field === "dealPricingType" && (value || "").trim() !== "SlotPricing") {
      setSlots((prev) =>
        prev.map((slot) => ({
          ...slot,
          pricingType: "NoDiscount",
          discountPercentage: null,
          isFree: false,
          customPrice: null,
          _isUnsaved: true,
        }))
      );
    }
  };

  const handlePricingTypeChange = (newPricingType) => {
    setFormState((prev) => ({
      ...prev,
      dealPricingType: newPricingType,
      dealPrice: newPricingType === "CustomPrice" ? prev.dealPrice : "",
      dealDiscountPercentage:
        newPricingType === "Discount" ? prev.dealDiscountPercentage : "",
    }));
    if ((newPricingType || "").trim() !== "SlotPricing") {
      setSlots((prev) =>
        prev.map((slot) => ({
          ...slot,
          pricingType: "NoDiscount",
          discountPercentage: null,
          isFree: false,
          customPrice: null,
          _isUnsaved: true,
        }))
      );
    }
  };

  const handleToggleChange = (field, value) => {
    setToggles((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSaveDeal = async (options = {}) => {
    const { exitAfterSave = true } = options; // default true for backward compat (Save and Exit)
    try {
      setSaving(true);

      // Every named slot must restrict choices to categories and/or items —
      // otherwise online ordering falls back to the full company menu.
      const slotsMissingSelection = (Array.isArray(slots) ? slots : []).filter((slot) => {
        if (!slot) return false;
        const hasName = slot.slotName && String(slot.slotName).trim();
        if (!hasName) return false;
        const hasCategories =
          Array.isArray(slot.allowedCategories) && slot.allowedCategories.length > 0;
        const hasItems = Array.isArray(slot.allowedItems) && slot.allowedItems.length > 0;
        return !hasCategories && !hasItems;
      });
      if (slotsMissingSelection.length > 0) {
        const names = slotsMissingSelection
          .map((s) => `"${String(s.slotName).trim()}"`)
          .join(", ");
        showErrorToast(
          `Each slot must have at least one category or item selected. Missing for: ${names}`
        );
        setSaving(false);
        return false;
      }

      // Auto-save only unsaved slots that have data
      const currentSlots = slots;
      if (Array.isArray(currentSlots) && currentSlots.length > 0) {
        for (let i = 0; i < currentSlots.length; i++) {
          const slot = currentSlots[i];
          const hasName = slot && slot.slotName && String(slot.slotName).trim();
          const isUnsavedSlot = slot && (slot.slotID === 0 || slot._isUnsaved === true);
          if (hasName && isUnsavedSlot) {
            try {
              await saveSlot(i);
            } catch (slotErr) {
              console.error("Auto-save slot failed:", slotErr);
              // Continue with other slots; deal save will still run
            }
          }
        }
      }

      const currentFormState = formStateRef.current;
      if (currentFormState.dealPricingType === "CustomPrice") {
        const priceVal = currentFormState.dealPrice;
        if (priceVal === undefined || priceVal === null || priceVal.toString().trim() === "") {
          showErrorToast("Deal price is required for Fixed Price");
          setSaving(false);
          return false;
        }
        if (parseFloat(priceVal) < 0) {
          showErrorToast("Deal price cannot be negative");
          setSaving(false);
          return false;
        }
      }

      // Upload image if there's a new file
      let imageUrl = imageState.dealImage;
      const baseURL = axiosInstance.defaults.baseURL || "";
      try {
        if (imageState.imageFile) {
          const imageFormData = new FormData();
          imageFormData.append("ImageFile", imageState.imageFile);
          const imageResponse = await axiosInstance.post(
            APIs.POST_UPLOAD_PRODUCT_IMAGE,
            imageFormData,
            { headers: { "Content-Type": "multipart/form-data" } }
          );
          const relativePath = imageResponse.data?.data?.filePath;
          if (relativePath) {
            // Store relative path in database (backend will add baseURL when fetching)
            imageUrl = relativePath;
          }
        } else if (imageState.dealImage && !imageState.dealImage.startsWith("http")) {
          // If image exists but is not a full URL, ensure it's a relative path
          // Remove base URL if it was accidentally included
          const baseUrlClean = baseURL.endsWith("/") ? baseURL.slice(0, -1) : baseURL;
          imageUrl = imageState.dealImage.replace(baseUrlClean, "").replace(/^\/+/, "");
        } else if (imageState.dealImage && imageState.dealImage.startsWith("http")) {
          // If it's a full URL, extract relative path for storage
          const baseUrlClean = baseURL.endsWith("/") ? baseURL.slice(0, -1) : baseURL;
          imageUrl = imageState.dealImage.replace(baseUrlClean, "").replace(/^\/+/, "");
        }
      } catch (err) {
        showErrorToast("Failed to upload image");
      }

      // Format dates properly for ASP.NET Core - use ISO format with T separator
      let formattedStartDate = null;
      let formattedEndDate = null;
      
      if (currentFormState.startDate) {
        // Handle both Date objects and moment objects
        const startDate = currentFormState.startDate instanceof Date 
          ? currentFormState.startDate 
          : currentFormState.startDate instanceof moment 
            ? currentFormState.startDate.toDate() 
            : new Date(currentFormState.startDate);
        
        const startMoment = moment(startDate);
        if (startMoment.isValid()) {
          // Format as ISO 8601: YYYY-MM-DDTHH:mm:ss (ASP.NET Core expects this format)
          formattedStartDate = startMoment.format("YYYY-MM-DDTHH:mm:ss");
        }
      }
      
      if (currentFormState.endDate) {
        // Handle both Date objects and moment objects
        const endDate = currentFormState.endDate instanceof Date 
          ? currentFormState.endDate 
          : currentFormState.endDate instanceof moment 
            ? currentFormState.endDate.toDate() 
            : new Date(currentFormState.endDate);
        
        const endMoment = moment(endDate);
        if (endMoment.isValid()) {
          formattedEndDate = endMoment.format("YYYY-MM-DDTHH:mm:ss");
        }
      }

      // Format weekly timings for the deal payload
      let dealWeeklyTimings = null;
      if (toggles.isScheduleDeal) {
        const formattedSchedules = [];
        if (useSameTimeSlots) {
          const days = [
            "Monday",
            "Tuesday",
            "Wednesday",
            "Thursday",
            "Friday",
            "Saturday",
            "Sunday",
          ];
          days.forEach((day) => {
            if (commonTimeSlot.length > 0) {
              commonTimeSlot.forEach((slot) => {
                if (
                  moment(slot.from, ["h:mm A"]).isValid() &&
                  moment(slot.to, ["h:mm A"]).isValid()
                ) {
                  formattedSchedules.push({
                    dealID: currentFormState.dealID,
                    compID: CompID,
                    dayName: day,
                    openTime: moment(slot.from, ["h:mm A"]).format("HH:mm"),
                    closeTime: moment(slot.to, ["h:mm A"]).format("HH:mm"),
                    updateDateTime: new Date().toISOString(),
                  });
                }
              });
            }
          });
        } else {
          schedules.forEach((schedule) => {
            if (schedule.checked && schedule.timeSlots.length > 0) {
              schedule.timeSlots.forEach((slot) => {
                if (
                  moment(slot.from, ["h:mm A"]).isValid() &&
                  moment(slot.to, ["h:mm A"]).isValid()
                ) {
                  formattedSchedules.push({
                    dealID: currentFormState.dealID,
                    compID: CompID,
                    dayName: schedule.day,
                    openTime: moment(slot.from, ["h:mm A"]).format("HH:mm"),
                    closeTime: moment(slot.to, ["h:mm A"]).format("HH:mm"),
                    updateDateTime: new Date().toISOString(),
                  });
                }
              });
            }
          });
        }
        
        if (formattedSchedules.length > 0) {
          dealWeeklyTimings = formattedSchedules;
        }
      }

      const pricingType = (currentFormState.dealPricingType || "").trim();
      const dealData = {
        dealID: currentFormState.dealID,
        compID: CompID,   
        dealName: currentFormState.dealName.trim(),
        dealDescription: currentFormState.dealDescription.trim() || null,
        dealImage: imageUrl || null,
        dealPrice: pricingType === "CustomPrice" && currentFormState.dealPrice ? parseFloat(currentFormState.dealPrice) : null,
        isActive: toggles.isActive,
        showInKiosk: toggles.showInKiosk,
        showInPOS: toggles.showInPOS,
        showInOnline: toggles.showInOnline,
        dealOrder: parseInt(currentFormState.dealOrder) || 0,
        pricingType: currentFormState.dealPricingType || null,
        discountPercentage: pricingType === "Discount" && currentFormState.dealDiscountPercentage ? parseFloat(currentFormState.dealDiscountPercentage) : null,
        autoDuplicateItem: toggles.autoDuplicateItem || false,
        minimumOrderValue: currentFormState.minimumOrderValue ? parseFloat(currentFormState.minimumOrderValue) : null,
        isScheduleDeal: toggles.isScheduleDeal || false,
        dealWeeklyTimings: dealWeeklyTimings,
        startDate: formattedStartDate,
        endDate: formattedEndDate,
        modifiedBy: modifiedBy,
      };

      const response = await axiosInstance.post(APIs.POST_ADD_OR_UPDATE_DEAL, dealData);

      if (response.data.status) {
        showSuccessToast("Deal updated successfully!");
        setInitialLoadedDataSnapshot(getDealSnapshot());
        if (exitAfterSave) {
          navigate("/deal/manageDeals");
        }
        return true;
      } else {
        showErrorToast(response.data.message || "Failed to update deal");
        return false;
      }
    } catch (error) {
      console.error("Error updating deal:", error);
      showErrorToast("Something went wrong. Please try again.");
      return false;
    } finally {
      setSaving(false);
    }
  };

  const addSlotRow = () => {
    const newSlot = {
      slotID: 0, // 0 means new slot
      dealID: formState.dealID,
      slotName: "",
      requiredQty: 0,
      slotOrder: slots.length,
      allowedCategories: [],
      allowedItems: [],
      constraint: null,
      buyQuantity: 0,
      pricingType: formState.dealPricingType === "SlotPricing" ? "NoDiscount" : null,
      discountPercentage: null,
      isFree: false,
      customPrice: null,
      autoDuplicateItem: false,
      _isUnsaved: true,
    };
    setSlots((prev) => {
      const newSlots = [...prev, newSlot];
      // Expand the new slot automatically
      setExpandedSlots((prevExpanded) => {
        const newExpanded = new Set(prevExpanded);
        newExpanded.add(newSlots.length - 1);
        return newExpanded;
      });
      return newSlots;
    });
  };

  const handleSlotChange = (index, field, value) => {
    setSlots((prev) => {
      const updatedSlots = [...prev];
      updatedSlots[index] = { ...updatedSlots[index], [field]: value, _isUnsaved: true };
      return updatedSlots;
    });
  };

  const handleConstraintChange = (index, field, value) => {
    const updatedSlots = [...slots];
    if (!updatedSlots[index].constraint) {
      updatedSlots[index].constraint = {
        minQty: null,
        maxQty: null,
        allowDuplicateItems: true,
        allowMixedCategories: true,
      };
    }
    updatedSlots[index].constraint = {
      ...updatedSlots[index].constraint,
      [field]: value,
    };
    updatedSlots[index]._isUnsaved = true;
    setSlots(updatedSlots);
  };

  const saveSlot = async (index) => {
    const slot = slots[index];
    
    if (!slot.slotName || !slot.slotName.trim()) {
      showErrorToast("Slot name is required");
      return;
    }

    const hasCategories = Array.isArray(slot.allowedCategories) && slot.allowedCategories.length > 0;
    const hasItems = Array.isArray(slot.allowedItems) && slot.allowedItems.length > 0;
    if (!hasCategories && !hasItems) {
      showErrorToast(
        `Select at least one category or item for slot "${slot.slotName.trim()}" before saving.`
      );
      return;
    }

    try {
      setSlotSaving(true);
      const slotData = {
        slotID: slot.slotID || 0,
        dealID: formState.dealID,
        slotName: slot.slotName.trim(),
        requiredQty: parseInt(slot.requiredQty) || 0,
        slotOrder: parseInt(slot.slotOrder) || 0,
        selectedVariant: (slot.selectedVariant || slot.SelectedVariant || "").toString().trim() || null,
        buyQuantity: slot.buyQuantity !== undefined && slot.buyQuantity !== null ? (parseInt(slot.buyQuantity) || 0) : 0,
        pricingType: formState.dealPricingType === "SlotPricing" ? (slot.pricingType || "NoDiscount") : null,
        discountPercentage: slot.discountPercentage ?? null,
        isFree: slot.isFree || false,
        customPrice: slot.customPrice != null && slot.customPrice !== "" ? (isNaN(parseFloat(slot.customPrice)) ? null : parseFloat(slot.customPrice)) : null,
        autoDuplicateItem: slot.autoDuplicateItem ?? false,
      };

      const response = await axiosInstance.post(APIs.POST_ADD_OR_UPDATE_DEAL_SLOT, slotData);

      if (response.data.status) {
        const savedSlot = response.data.data;
        
        // Save constraints if they exist
        if (slot.constraint) {
          await axiosInstance.post(APIs.POST_ADD_OR_UPDATE_DEAL_SLOT_CONSTRAINT, {
            slotID: savedSlot.slotID,
            minQty: slot.constraint.minQty ? parseInt(slot.constraint.minQty) : null,
            maxQty: slot.constraint.maxQty ? parseInt(slot.constraint.maxQty) : null,
            allowDuplicateItems: slot.constraint.allowDuplicateItems !== false,
            allowMixedCategories: slot.constraint.allowMixedCategories !== false,
          });
        }
        
        // Save categories if they exist (for new slots)
        if (slot.allowedCategories && slot.allowedCategories.length > 0) {
          const categoryPromises = slot.allowedCategories.map(cat => {
            const categoryID = cat.categoryID || cat.CategoryID;
            if (!categoryID) return null;
            return axiosInstance.post(APIs.POST_ADD_DEAL_SLOT_ALLOWED_CATEGORY, {
              slotID: savedSlot.slotID,
              categoryID: categoryID,
            });
          });
          await Promise.all(categoryPromises.filter(p => p !== null));
        }
        
        // Save items if they exist (for new slots)
        if (slot.allowedItems && slot.allowedItems.length > 0) {
          const itemPromises = slot.allowedItems.map(item => {
            const itemID = item.itemID || item.ItemID;
            if (!itemID) return null;
            return axiosInstance.post(APIs.POST_ADD_DEAL_SLOT_ALLOWED_ITEM, {
              slotID: savedSlot.slotID,
              itemID: itemID,
            });
          });
          await Promise.all(itemPromises.filter(p => p !== null));
        }
        
        showSuccessToast(slot.slotID === 0 ? "Slot created successfully!" : "Slot updated successfully!");
        // Close the slot card after successful save
        setExpandedSlots((prev) => {
          const newExpanded = new Set(prev);
          newExpanded.delete(index);
          return newExpanded;
        });
        // Refresh slots to get updated data
        await fetchSlots(formState.dealID, null, formStateRef.current.dealPricingType);
      } else {
        showErrorToast(response.data.message || "Failed to save slot");
      }
      setSlotSaving(false);
    } catch (error) {
      console.error("Error saving slot:", error);
      showErrorToast("Error saving slot");
      setSlotSaving(false);
    }
  };

  const removeSlotRow = async (slot, index) => {
    try {
      const confirmDelete = await swal({
        title: "Are you sure?",
        text: `Are you sure you want to delete slot "${slot.slotName || "this slot"}"? All associated categories, items, and constraints will also be deleted.`,
        icon: "warning",
        buttons: true,
        dangerMode: true,
      });

      if (!confirmDelete) return;

      if (slot.slotID > 0) {
        // Existing slot - delete from database
        setSlotSaving(true);
        const response = await axiosInstance.delete(APIs.DELETE_DEAL_SLOT, {
          params: { SlotID: slot.slotID },
        });

        if (response.data.status) {
          showSuccessToast("Slot deleted successfully!");
          setSlots((prev) => prev.filter((_, i) => i !== index));
          // Remove from expanded set
          setExpandedSlots((prev) => {
            const newExpanded = new Set(prev);
            newExpanded.delete(index);
            return newExpanded;
          });
        } else {
          showErrorToast(response.data.message || "Deletion failed");
        }
        setSlotSaving(false);
      } else {
        // New slot - just remove from local state
        setSlots((prev) => prev.filter((_, i) => i !== index));
        // Remove from expanded set
        setExpandedSlots((prev) => {
          const newExpanded = new Set(prev);
          newExpanded.delete(index);
          return newExpanded;
        });
      }
    } catch (err) {
      showErrorToast("Failed to delete slot");
      setSlotSaving(false);
    }
  };

  const handleManageCategories = useCallback((slot, index) => {
    if (!slot) {
      console.error("handleManageCategories: slot is null or undefined");
      return;
    }
    
    // Find slot index if not provided
    const slotIndex = index !== undefined ? index : slots.findIndex(s => 
      s && (s.slotID === slot.slotID && s.slotID !== 0 ? true : 
      (s.slotName === slot.slotName && s.requiredQty === slot.requiredQty && s.slotOrder === slot.slotOrder))
    );
    
    selectedSlotRef.current = slot; // Store in ref FIRST
    selectedSlotIndexRef.current = slotIndex; // Store index
    setSelectedSlot(slot);
    
    // Check if slot already has categories or items
    const hasCategories = slot.allowedCategories && slot.allowedCategories.length > 0;
    const hasItems = slot.allowedItems && slot.allowedItems.length > 0;
    
    // If slot has neither, show type selection modal
    if (!hasCategories && !hasItems) {
      setShowTypeSelectionModal(true);
      return;
    }
    
    // If slot already has categories, directly show category modal
    if (hasCategories) {
      // Ensure slot is preserved in ref (already set above, but ensure it's current)
      selectedSlotRef.current = slot;
      selectedSlotIndexRef.current = slotIndex;
      setSelectedSlot(slot);
      const categories = slot.allowedCategories.map(cat => resolveCategoryForSelector(cat));
      setSelectedCategories(categories);
      setSelectedType('categories');
      setShowCategoryModal(true);
      return;
    }
    
    // If slot has items but user wants to switch to categories, show confirmation
    if (hasItems) {
      swal({
        title: "Switch to Categories?",
        text: "This slot currently has items selected. Switching to categories will remove all selected items. Do you want to continue?",
        icon: "warning",
        buttons: true,
        dangerMode: true,
      }).then((confirmed) => {
        if (confirmed) {
          // Ensure slot is preserved in ref before opening modal
          selectedSlotRef.current = slot;
          selectedSlotIndexRef.current = slotIndex;
          setSelectedSlot(slot);
          // Clear items first
          setSelectedCategories([]);
          setSelectedType('categories');
          setShowCategoryModal(true);
        }
      });
      return;
    }
  }, [allCategories, slots]);

  const handleSaveCategories = useCallback(async (selectedCats) => {
    // Use ref to get current slot value (preserved even if state changes)
    let currentSlot = selectedSlotRef.current || selectedSlot;
    
    // Fallback: if slot is null, try to find it from slots array using the index ref
    if (!currentSlot && selectedSlotIndexRef.current !== null && selectedSlotIndexRef.current >= 0) {
      currentSlot = slots[selectedSlotIndexRef.current];
      if (currentSlot) {
        // Restore the refs
        selectedSlotRef.current = currentSlot;
        setSelectedSlot(currentSlot);
      }
    }
    
    if (!currentSlot) {
      console.error("No selected slot for saving categories. selectedSlot:", selectedSlot, "ref:", selectedSlotRef.current, "indexRef:", selectedSlotIndexRef.current);
      showErrorToast("No slot selected. Please try again.");
      return;
    }
    
    const slotIndex = selectedSlotIndexRef.current !== null ? selectedSlotIndexRef.current : 
      slots.findIndex(s => 
        s && (currentSlot.slotID !== 0 
          ? s.slotID === currentSlot.slotID 
          : (s.slotName === currentSlot.slotName && s.requiredQty === currentSlot.requiredQty && s.slotOrder === currentSlot.slotOrder))
      );

    if (slotIndex === -1) {
      console.error("Slot not found in slots array. slot:", currentSlot, "slots:", slots);
      showErrorToast("Slot not found. Please try again.");
      return;
    }

    try {
      const slotIDToRefresh = currentSlot.slotID;
      
      // Use selectedCats from CategorySelector callback (it's passed directly)
      const categoriesToSave = selectedCats || selectedCategories;
      console.log("Saving categories:", categoriesToSave, "for slot:", currentSlot.slotID);
      
      if (!categoriesToSave || !Array.isArray(categoriesToSave)) {
        console.error("Invalid categories data:", categoriesToSave);
        showErrorToast("Invalid category data");
        return;
      }
      
      const normalizedCategories = [];
      const seenCategoryIds = new Set();
      categoriesToSave.forEach((cat) => {
        const categoryID = getCategoryId(cat);
        if (categoryID == null || categoryID === "" || seenCategoryIds.has(String(categoryID))) {
          return;
        }
        seenCategoryIds.add(String(categoryID));
        normalizedCategories.push({
          id: null,
          slotID: currentSlot.slotID,
          categoryID,
          categoryName: getCategoryDisplayName(cat) || null,
        });
      });

      // Update local state IMMEDIATELY (optimistic update) - like updateItem.jsx
      if (slotIndex !== -1) {
        
        setSlots(prev => prev.map((slot, idx) => 
          idx === slotIndex 
            ? {
                ...slot,
                allowedCategories: normalizedCategories,
                allowedItems: [], // Clear items (mutually exclusive)
                _isUnsaved: true,
              }
            : slot
        ));
      }
      
      // For new slots (slotID: 0), only update local state - don't save to backend yet
      // User needs to save the slot first, then categories will be saved
      if (currentSlot.slotID === 0) {
        // Close modals after updating local state
        setShowCategoryModal(false);
        setShowTypeSelectionModal(false);
        setSelectedSlot(null);
        setSelectedType(null);
        selectedSlotRef.current = null;
        selectedSlotIndexRef.current = null;
        typeSelectionInProgressRef.current = false;
        showSuccessToast("Categories selected! Please save the slot to persist changes.");
        return;
      }
      
      // For existing slots, save to backend
      // Delete existing categories AND items (mutually exclusive) from backend
      await Promise.all([
        axiosInstance.delete(APIs.DELETE_DEAL_SLOT_ALLOWED_CATEGORIES_BY_SLOT_ID, {
          params: { SlotID: currentSlot.slotID },
        }),
        axiosInstance.delete(APIs.DELETE_DEAL_SLOT_ALLOWED_ITEMS_BY_SLOT_ID, {
          params: { SlotID: currentSlot.slotID },
        }),
      ]);

      // Add new categories to backend
      if (normalizedCategories.length > 0) {
        const savePromises = normalizedCategories.map((row) => {
          const categoryID = row.categoryID;
          if (!categoryID) {
            console.error("Category missing ID:", row);
            return null;
          }
          const payload = {
            slotID: currentSlot.slotID,
            categoryID: categoryID,
          };
          console.log("Saving category payload:", payload);
          return axiosInstance.post(APIs.POST_ADD_DEAL_SLOT_ALLOWED_CATEGORY, payload);
        });
        
        // Filter out null promises
        const validPromises = savePromises.filter(p => p !== null);
        if (validPromises.length > 0) {
          await Promise.all(validPromises);
        }
      }

      showSuccessToast("Categories updated successfully!");
      
      // Refresh slots data from backend to ensure sync (keep this slot expanded)
      await fetchSlots(formState.dealID, slotIDToRefresh, formStateRef.current.dealPricingType);
      
      // Close all modals after save completes
      setShowCategoryModal(false);
      setShowTypeSelectionModal(false);
      setSelectedSlot(null);
      setSelectedType(null);
      selectedSlotRef.current = null;
      selectedSlotIndexRef.current = null;
      typeSelectionInProgressRef.current = false;
    } catch (error) {
      console.error("Error saving categories:", error);
      showErrorToast("Failed to save categories: " + (error.response?.data?.message || error.message));
      // Revert on error by refreshing from backend (keep this slot expanded)
      await fetchSlots(formState.dealID, currentSlot?.slotID ?? null, formStateRef.current.dealPricingType);
      // Close modals even on error
      setShowCategoryModal(false);
      setShowTypeSelectionModal(false);
      setSelectedSlot(null);
      setSelectedType(null);
      selectedSlotRef.current = null;
      selectedSlotIndexRef.current = null;
      typeSelectionInProgressRef.current = false;
    }
  }, [selectedSlot, selectedCategories, formState.dealID, fetchSlots, slots]);

  const handleManageItems = useCallback((slot, index) => {
    if (!slot) {
      console.error("handleManageItems: slot is null or undefined");
      return;
    }
    
    // Find slot index if not provided
    const slotIndex = index !== undefined ? index : slots.findIndex(s => 
      s && (s.slotID === slot.slotID && s.slotID !== 0 ? true : 
      (s.slotName === slot.slotName && s.requiredQty === slot.requiredQty && s.slotOrder === slot.slotOrder))
    );
    
    selectedSlotRef.current = slot; // Store in ref FIRST
    selectedSlotIndexRef.current = slotIndex; // Store index
    setSelectedSlot(slot);
    
    // Check if slot already has categories or items
    const hasCategories = slot.allowedCategories && slot.allowedCategories.length > 0;
    const hasItems = slot.allowedItems && slot.allowedItems.length > 0;
    
    // If slot has neither, show type selection modal
    if (!hasCategories && !hasItems) {
      setShowTypeSelectionModal(true);
      return;
    }
    
    // If slot already has items, directly show item modal
    if (hasItems) {
      // Ensure slot is preserved in ref (already set above, but ensure it's current)
      selectedSlotRef.current = slot;
      selectedSlotIndexRef.current = slotIndex;
      setSelectedSlot(slot);
      // ProductMultiSelectionModal expects IDs; it can also accept objects but normalizes to IDs anyway.
      // We'll pass IDs to keep the shape consistent.
      const itemIds = slot.allowedItems
        .map((it) => {
          const raw =
            typeof it === "object"
              ? (it.itemID || it.ItemID || it.menuItem_ID || it.MenuItem_ID || it.id)
              : it;
          const id = raw !== undefined && raw !== null ? parseInt(raw, 10) : null;
          return id || null;
        })
        .filter(Boolean);
      setSelectedItems(itemIds);
      setSelectedType('items');
      setShowItemModal(true);
      return;
    }
    
    // If slot has categories but user wants to switch to items, show confirmation
    if (hasCategories) {
      swal({
        title: "Switch to Items?",
        text: "This slot currently has categories selected. Switching to items will remove all selected categories. Do you want to continue?",
        icon: "warning",
        buttons: true,
        dangerMode: true,
      }).then((confirmed) => {
        if (confirmed) {
          // Ensure slot is preserved in ref before opening modal
          selectedSlotRef.current = slot;
          selectedSlotIndexRef.current = slotIndex;
          setSelectedSlot(slot);
          // Clear categories first
          setSelectedItems([]);
          setSelectedType('items');
          setShowItemModal(true);
        }
      });
      return;
    }
  }, [allItems, slots]);
  
  const handleTypeSelection = useCallback((type) => {
    const currentSlot = selectedSlotRef.current || selectedSlot;
    if (!currentSlot) {
      console.error("No slot selected for type selection");
      return;
    }
    
    // Mark that type selection is in progress - prevents onClose from clearing slot
    typeSelectionInProgressRef.current = true;
    
    // Ensure slot is preserved in ref before opening modal
    // This prevents it from being cleared by onClose callback
    selectedSlotRef.current = currentSlot;
    setSelectedSlot(currentSlot);
    
    setSelectedType(type);
    
    if (type === 'categories') {
      // Convert allowed categories to the format expected by CategorySelector
      const categories = currentSlot.allowedCategories?.map(cat =>
        resolveCategoryForSelector(cat)
      ) || [];
      setSelectedCategories(categories);
      // Open modal immediately - onClose will check ref to know we're transitioning
      setShowCategoryModal(true);
    } else if (type === 'items') {
      // Convert allowed items to the format expected by ProductMultiSelectionModal
      const items = currentSlot.allowedItems?.map(item => {
        const itemID = item.itemID || item.ItemID;
        const menuItem = allItems.find(i => i.menuItem_ID === itemID);
        return menuItem || { menuItem_ID: itemID, menuItem_Name: `Item ${itemID}` };
      }) || [];
      setSelectedItems(items);
      // Open modal immediately - onClose will check ref to know we're transitioning
      setShowItemModal(true);
    }
    
    // Reset flag after a short delay to allow modal to open
    setTimeout(() => {
      typeSelectionInProgressRef.current = false;
    }, 200);
  }, [selectedSlot, allCategories, allItems]);

  const handleSaveItems = useCallback(async (selectedItemsFromModal) => {
    // Use ref to get current slot value (preserved even if state changes)
    let currentSlot = selectedSlotRef.current || selectedSlot;
    
    // Fallback: if slot is null, try to find it from slots array using the index ref
    if (!currentSlot && selectedSlotIndexRef.current !== null && selectedSlotIndexRef.current >= 0) {
      currentSlot = slots[selectedSlotIndexRef.current];
      if (currentSlot) {
        // Restore the refs
        selectedSlotRef.current = currentSlot;
        setSelectedSlot(currentSlot);
      }
    }
    
    if (!currentSlot) {
      console.error("No selected slot for saving items. selectedSlot:", selectedSlot, "ref:", selectedSlotRef.current, "indexRef:", selectedSlotIndexRef.current);
      showErrorToast("No slot selected. Please try again.");
      return;
    }
    
    const slotIndex = selectedSlotIndexRef.current !== null ? selectedSlotIndexRef.current : 
      slots.findIndex(s => 
        s && (currentSlot.slotID !== 0 
          ? s.slotID === currentSlot.slotID 
          : (s.slotName === currentSlot.slotName && s.requiredQty === currentSlot.requiredQty && s.slotOrder === currentSlot.slotOrder))
      );

    if (slotIndex === -1) {
      console.error("Slot not found in slots array. slot:", currentSlot, "slots:", slots);
      showErrorToast("Slot not found. Please try again.");
      return;
    }

    try {
      const slotIDToRefresh = currentSlot.slotID;
      
      // ProductMultiSelectionModal returns an array of IDs (numbers/strings), not objects
      const itemsToSave = selectedItemsFromModal || selectedItems;
      console.log("Saving items:", itemsToSave, "for slot:", currentSlot.slotID);
      
      if (!itemsToSave || !Array.isArray(itemsToSave)) {
        console.error("Invalid items data:", itemsToSave);
        showErrorToast("Invalid item data");
        return;
      }
      
      // Update local state IMMEDIATELY (optimistic update) - like updateItem.jsx
      if (slotIndex !== -1) {
        const normalizedItems = itemsToSave
          .map((item) => {
            const itemIDRaw =
              typeof item === "object"
                ? (item.menuItem_ID || item.MenuItem_ID || item.itemID || item.ItemID || item.id)
                : item;
            const itemID = itemIDRaw !== undefined && itemIDRaw !== null ? parseInt(itemIDRaw, 10) : null;
            return itemID
              ? {
                  id: null,
                  slotID: currentSlot.slotID,
                  itemID: itemID,
                }
              : null;
          })
          .filter(Boolean);
        
        setSlots(prev => prev.map((slot, idx) => 
          idx === slotIndex 
            ? {
                ...slot,
                allowedItems: normalizedItems,
                allowedCategories: [], // Clear categories (mutually exclusive)
                _isUnsaved: true,
              }
            : slot
        ));
      }
      
      // For new slots (slotID: 0), only update local state - don't save to backend yet
      // User needs to save the slot first, then items will be saved
      if (currentSlot.slotID === 0) {
        // Close modals after updating local state
        setShowItemModal(false);
        setShowTypeSelectionModal(false);
        setSelectedSlot(null);
        setSelectedType(null);
        selectedSlotRef.current = null;
        selectedSlotIndexRef.current = null;
        typeSelectionInProgressRef.current = false;
        showSuccessToast("Items selected! Please save the slot to persist changes.");
        return;
      }
      
      // For existing slots, save to backend
      // Delete existing items AND categories (mutually exclusive) from backend
      await Promise.all([
        axiosInstance.delete(APIs.DELETE_DEAL_SLOT_ALLOWED_ITEMS_BY_SLOT_ID, {
          params: { SlotID: currentSlot.slotID },
        }),
        axiosInstance.delete(APIs.DELETE_DEAL_SLOT_ALLOWED_CATEGORIES_BY_SLOT_ID, {
          params: { SlotID: currentSlot.slotID },
        }),
      ]);

      // Add new items to backend
      if (itemsToSave.length > 0) {
        const savePromises = itemsToSave.map(item => {
          const itemIDRaw =
            typeof item === "object"
              ? (item.menuItem_ID || item.MenuItem_ID || item.itemID || item.ItemID || item.id)
              : item;
          const itemID = itemIDRaw !== undefined && itemIDRaw !== null ? parseInt(itemIDRaw, 10) : null;
          if (!itemID) {
            console.error("Item missing ID:", item);
            return null;
          }
          const payload = {
            slotID: currentSlot.slotID,
            itemID: itemID,
          };
          console.log("Saving item payload:", payload);
          return axiosInstance.post(APIs.POST_ADD_DEAL_SLOT_ALLOWED_ITEM, payload);
        });
        
        // Filter out null promises
        const validPromises = savePromises.filter(p => p !== null);
        if (validPromises.length > 0) {
          await Promise.all(validPromises);
        }
      }

      showSuccessToast("Items updated successfully!");
      
      // Refresh slots data from backend to ensure sync (keep this slot expanded)
      await fetchSlots(formState.dealID, currentSlot.slotID, formStateRef.current.dealPricingType);
      
      // Close all modals after save completes
      setShowItemModal(false);
      setShowTypeSelectionModal(false);
      setSelectedSlot(null);
      setSelectedType(null);
      selectedSlotRef.current = null;
      selectedSlotIndexRef.current = null;
      typeSelectionInProgressRef.current = false;
    } catch (error) {
      console.error("Error saving items:", error);
      showErrorToast("Failed to save items: " + (error.response?.data?.message || error.message));
      // Revert on error by refreshing from backend (keep this slot expanded)
      await fetchSlots(formState.dealID, currentSlot?.slotID ?? null, formStateRef.current.dealPricingType);
      // Close modals even on error
      setShowItemModal(false);
      setShowTypeSelectionModal(false);
      setSelectedSlot(null);
      setSelectedType(null);
      selectedSlotRef.current = null;
      selectedSlotIndexRef.current = null;
      typeSelectionInProgressRef.current = false;
    }
  }, [selectedSlot, selectedItems, formState.dealID, fetchSlots, slots]);



  const handleDeleteCategory = async (categoryID, slotID) => {
    try {
      const response = await axiosInstance.get(APIs.GET_DEAL_SLOT_ALLOWED_CATEGORIES, {
        params: { SlotID: slotID },
      });

      if (response.data.status && response.data.data) {
        // Handle both camelCase and PascalCase from API
        const category = response.data.data.find(c => 
          (c.categoryID === categoryID) || (c.CategoryID === categoryID)
        );
        if (category) {
          const categoryDbID = category.id || category.ID;
          await axiosInstance.delete(APIs.DELETE_DEAL_SLOT_ALLOWED_CATEGORY, {
            params: { ID: categoryDbID },
          });
          showSuccessToast("Category removed successfully!");
          await fetchSlots(formState.dealID, slotID, formStateRef.current.dealPricingType);
        }
      }
    } catch (error) {
      console.error("Error deleting category:", error);
      showErrorToast("Failed to remove category");
    }
  };

  const handleDeleteItem = async (itemID, slotID) => {
    try {
      const response = await axiosInstance.get(APIs.GET_DEAL_SLOT_ALLOWED_ITEMS, {
        params: { SlotID: slotID },
      });

      if (response.data.status && response.data.data) {
        // Handle both camelCase and PascalCase from API
        const item = response.data.data.find(i => 
          (i.itemID === itemID) || (i.ItemID === itemID)
        );
        if (item) {
          const itemDbID = item.id || item.ID;
          await axiosInstance.delete(APIs.DELETE_DEAL_SLOT_ALLOWED_ITEM, {
            params: { ID: itemDbID },
          });
          showSuccessToast("Item removed successfully!");
          await fetchSlots(formState.dealID, slotID, formStateRef.current.dealPricingType);
        }
      }
    } catch (error) {
      console.error("Error deleting item:", error);
      showErrorToast("Failed to remove item");
    }
  };

  const getCategoryId = (category) =>
    category?.rootCat_ID ??
    category?.RootCat_ID ??
    category?.categoryID ??
    category?.CategoryID ??
    category?.categoryId ??
    category?.rC_ID ??
    category?.RC_ID ??
    null;

  const getCategoryDisplayName = (category) =>
    category?.rootCat_Name ||
    category?.RootCat_Name ||
    category?.categoryName ||
    category?.CategoryName ||
    category?.rC_Name ||
    category?.RC_Name ||
    null;

  /** Search root + nested SubCategoriesList (slot categories can be subcategories). */
  const findCategoryById = useCallback((categoryID, categories = allCategories) => {
    if (categoryID == null || categoryID === "") return null;
    const target = String(categoryID);
    const walk = (list) => {
      if (!Array.isArray(list)) return null;
      for (const cat of list) {
        if (!cat) continue;
        if (String(getCategoryId(cat) ?? "") === target) return cat;
        const nested =
          cat.subCategoriesList ||
          cat.SubCategoriesList ||
          cat.subCategories ||
          cat.SubCategories;
        const found = walk(nested);
        if (found) return found;
      }
      return null;
    };
    return walk(categories);
  }, [allCategories]);

  const getCategoryName = (categoryID) => {
    const category = findCategoryById(categoryID);
    return getCategoryDisplayName(category) || `Category ${categoryID}`;
  };

  const resolveCategoryForSelector = (cat) => {
    const categoryID = cat?.categoryID ?? cat?.CategoryID;
    const category = findCategoryById(categoryID);
    if (category) return category;
    const knownName = getCategoryDisplayName(cat);
    return {
      rootCat_ID: categoryID,
      rootCat_Name: knownName || `Category ${categoryID}`,
    };
  };

  const getItemName = (itemID) => {
    const id = parseInt(itemID, 10);
    const item = allItems.find(i => parseInt(i.menuItem_ID || i.MenuItem_ID || i.id, 10) === id);
    return item ? (item.menuItem_Name || item.MenuItem_Name || item.name) : `Item ${itemID}`;
  };

  useUnsavedChangesGuard({
    isDirty: !loading && isDirty,
    onSave: () => handleSaveDeal({ exitAfterSave: false }),
    onDiscard: () => setInitialLoadedDataSnapshot(getDealSnapshot()),
  });

  if (loading) {
    return (
      <div className="p-3">
        <GridSectionLoader loading={true} minHeight="70vh" />
      </div>
    );
  }

  return (
    <div className="deal-form-page" style={{ "--df-accent": themeColor }}>
      <div className="deal-form-page__head">
        <div>
          <ol className="breadcrumb">
            <li className="breadcrumb-item">
              <Link to="/">Home</Link>
            </li>
            <li className="breadcrumb-item">
              <Link to="/deal/manageDeals">Manage Deals</Link>
            </li>
            <li className="breadcrumb-item active">Update Deal</li>
          </ol>
          <h1 className="deal-form-page__title">
            <i className="fa fa-tags deal-form-page__title-icon" aria-hidden="true" />
            Update Deal
          </h1>
          <p className="deal-form-page__sub">
            {formState.dealName
              ? formState.dealName
              : "Edit deal details, weekly schedule, and slots."}
          </p>
        </div>
        <div>
          <div className="deal-form-page__actions">
            <button
              type="button"
              className="deal-form-page__cta"
              style={{
                background: "#dc3545",
                border: "none",
                color: "#fff",
              }}
              onClick={() => navigate("/deal/manageDeals")}
              disabled={saving}
            >
              Cancel
            </button>
            <button
              type="button"
              className="deal-form-page__cta deal-form-page__cta--secondary"
              onClick={() => handleSaveDeal({ exitAfterSave: false })}
              disabled={saving}
            >
              <i className="fa fa-save" aria-hidden="true" />
              {saving ? "Saving..." : "Save"}
            </button>
            <button
              type="button"
              className="deal-form-page__cta"
              onClick={() => handleSaveDeal({ exitAfterSave: true })}
              disabled={saving}
            >
              <i className="fa fa-save" aria-hidden="true" />
              {saving ? "Saving..." : "Save and Exit"}
            </button>
          </div>
        </div>
      </div>

      <ul className="nav nav-tabs deal-tabs" role="tablist">
        <li className="nav-item" role="presentation">
          <button
            type="button"
            role="tab"
            aria-selected={activeTab === "basics"}
            className={`nav-link ${activeTab === "basics" ? "active" : ""}`}
            onClick={() => setActiveTab("basics")}
          >
            <i className="fa fa-info-circle" aria-hidden="true" />
            Basics
          </button>
        </li>
        <li className="nav-item" role="presentation">
          <button
            type="button"
            role="tab"
            aria-selected={activeTab === "schedule"}
            className={`nav-link ${activeTab === "schedule" ? "active" : ""}`}
            onClick={() => setActiveTab("schedule")}
          >
            <i className="fa fa-clock" aria-hidden="true" />
            Schedule
          </button>
        </li>
        <li className="nav-item" role="presentation">
          <button
            type="button"
            role="tab"
            aria-selected={activeTab === "slots"}
            className={`nav-link ${activeTab === "slots" ? "active" : ""}`}
            onClick={() => setActiveTab("slots")}
          >
            <i className="fa fa-list-ul" aria-hidden="true" />
            Slots
            <span className="deal-tab-count">{slots.filter(Boolean).length}</span>
          </button>
        </li>
      </ul>

      <GridSectionLoader loading={saving} minHeight="400px">
        {activeTab === "basics" && (
          <DealFormBasics
            themeColor={themeColor}
            formState={formState}
            toggles={toggles}
            imageState={imageState}
            setImageState={setImageState}
            handleInputChange={handleInputChange}
            handlePricingTypeChange={handlePricingTypeChange}
            handleToggleChange={handleToggleChange}
            dropdownStyles={dropdownStyles}
            getCurrencySymbol={() => getCurrencySymbol(compCurrency)}
            showMinimumOrder={true}
          />
        )}

        {activeTab === "schedule" && (
          <div className="deal-form-shell" style={{ "--df-accent": themeColor }}>
            <DealSection icon="fa-calendar-week" label="Weekly schedule">
              <DealToggleRow
                isOn={!!toggles.isScheduleDeal}
                title="Set schedule"
                hint="Set the days of the week and times of day this deal is available. (Example: Happy Hour 4pm-6pm)"
                footer={
                  toggles.isScheduleDeal ? (
                    <div>
                      <div className="mb-3">
                        <DealToggleRow
                          isOn={useSameTimeSlots}
                          title="Use same time slots for all days"
                          hint="Apply one time range to every day of the week"
                        >
                          <ToggleSwitch
                            isChecked={useSameTimeSlots}
                            onToggle={() => {
                              setUseSameTimeSlots(!useSameTimeSlots);
                              if (!useSameTimeSlots) {
                                applyCommonTimeSlotToAll();
                              }
                            }}
                            label=""
                            disabled={false}
                            themeColor={themeColor}
                          />
                        </DealToggleRow>
                      </div>
                      <div className="deal-schedule-body" style={{ position: "relative", zIndex: 1000, overflow: "visible" }}>
                        {useSameTimeSlots ? (
                          <CommonTimeSlotForAllWeek
                            setCommonTimeSlots={setCommonTimeSlot}
                            commonTimeSlots={commonTimeSlot}
                            isProfilePage={false}
                          />
                        ) : (
                          <ScheduleManager
                            schedules={schedules}
                            setSchedules={setSchedules}
                            isProfilePage={false}
                            isDarkMode={isDarkMode}
                          />
                        )}
                      </div>
                    </div>
                  ) : null
                }
              >
                <ToggleSwitch
                  label=""
                  isChecked={toggles.isScheduleDeal || false}
                  onToggle={(checked) => handleToggleChange("isScheduleDeal", checked)}
                  themeColor={themeColor}
                />
              </DealToggleRow>
            </DealSection>
          </div>
        )}

        {activeTab === "slots" && (
          <>
      <div className="deal-form-shell" style={{ "--df-accent": themeColor }}>
        <DealSection
          icon="fa-list-ul"
          label="Deal Slots"
          headerRight={
            <button
              type="button"
              className="deal-form-page__cta"
              onClick={addSlotRow}
              disabled={slotSaving}
            >
              <i className="fa fa-plus" aria-hidden="true" />
              Add Slot
            </button>
          }
        >
          {slots.length === 0 ? (
            <div 
              className="deal-slots-empty">
              <div className="deal-slots-empty__icon" aria-hidden="true">
                <i className="fa fa-inbox"></i>
              </div>
              <p className="deal-slots-empty__text">
                No slots configured. Click "Add Slot" to create one.
              </p>
            </div>
          ) : (
            <div className="deal-slots-list">
              {slots.map((slot, index) => {
                // Skip null/undefined slots
                if (!slot) return null;
                
                return (
                  <div
                    key={slot.slotID || `new-${index}`}
                  className="deal-slot-card">
                  {/* Slot Header */}
                  <div
                    className="deal-slot-card__header"
                    onClick={() => {
                      const newExpanded = new Set(expandedSlots);
                      if (newExpanded.has(index)) {
                        newExpanded.delete(index);
                      } else {
                        newExpanded.add(index);
                      }
                      setExpandedSlots(newExpanded);
                    }}
                  >
                    <div className="d-flex justify-content-between align-items-center">
                      <div className="d-flex align-items-center gap-3" style={{ flex: 1 }}>
                        <button
                          type="button"
                          className="btn btn-sm"
                          style={{
                            background: 'transparent',
                            border: 'none',
                            color: isDarkMode ? '#ffffff' : '#000000',
                            padding: '4px 8px',
                            minWidth: 'auto'
                          }}
                          onClick={(e) => {
                            e.stopPropagation();
                            const newExpanded = new Set(expandedSlots);
                            if (newExpanded.has(index)) {
                              newExpanded.delete(index);
                            } else {
                              newExpanded.add(index);
                            }
                            setExpandedSlots(newExpanded);
                          }}
                        >
                          <i 
                            className={`fa fa-chevron-${expandedSlots.has(index) ? 'up' : 'down'}`}
                            style={{
                              transition: 'transform 0.3s ease',
                              fontSize: '14px'
                            }}
                          ></i>
                        </button>
                        <div 
                          style={{
                            width: '40px',
                            height: '40px',
                            borderRadius: '10px',
                            background: `linear-gradient(135deg, ${themeColor} 0%, ${themeColor}dd 100%)`,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            boxShadow: `0 2px 8px ${themeColor}30`
                          }}
                        >
                          <i className="fa fa-list-alt text-white"></i>
                        </div>
                        <div>
                          <h6 className="mb-1" style={{ 
                            color: isDarkMode ? '#ffffff' : '#000000',
                            fontWeight: '600',
                            fontSize: '1.1rem'
                          }}>
                            {slot.slotName || `New Slot ${index + 1}`}
                          </h6>
                          <div className="d-flex align-items-center gap-2 flex-wrap">
                            <span 
                              className="badge" 
                              style={{
                                backgroundColor: '#4299e1',
                                color: 'white',
                                padding: '4px 10px',
                                borderRadius: '6px',
                                fontSize: '0.75rem',
                                fontWeight: '500'
                              }}
                            >
                              <i className="fa fa-sort-numeric-up me-1"></i>
                              Required: {slot.requiredQty || 1}
                            </span>
                            {slot.slotID > 0 && (
                              <span 
                                className="badge d-none" 
                                style={{
                                  backgroundColor: '#17a2b8',
                                  color: 'white',
                                  padding: '4px 10px',
                                  borderRadius: '6px',
                                  fontSize: '0.75rem',
                                  fontWeight: '500'
                                }}
                              >
                                <i className="fa fa-sort me-1"></i>
                                Order: {slot.slotOrder || 0}
                              </span>
                            )}
                            {slot.slotID === 0 && (
                              <span 
                                className="badge" 
                                style={{
                                  backgroundColor: '#ffc107',
                                  color: '#000',
                                  padding: '4px 10px',
                                  borderRadius: '6px',
                                  fontSize: '0.75rem',
                                  fontWeight: '500'
                                }}
                              >
                                <i className="fa fa-edit me-1"></i>
                                New
                              </span>
                            )}
                            {slot.allowedCategories && slot.allowedCategories.length > 0 && (
                              <span 
                                className="badge" 
                                style={{
                                  backgroundColor: '#4299e1',
                                  color: 'white',
                                  padding: '4px 10px',
                                  borderRadius: '6px',
                                  fontSize: '0.75rem',
                                  fontWeight: '500'
                                }}
                              >
                                <i className="fa fa-tags me-1"></i>
                                {slot.allowedCategories.length} Categories
                              </span>
                            )}
                            {slot.allowedItems && slot.allowedItems.length > 0 && (
                              <span 
                                className="badge" 
                                style={{
                                  backgroundColor: '#48bb78',
                                  color: 'white',
                                  padding: '4px 10px',
                                  borderRadius: '6px',
                                  fontSize: '0.75rem',
                                  fontWeight: '500'
                                }}
                              >
                                <i className="fa fa-box me-1"></i>
                                {slot.allowedItems.length} Items
                              </span>
                            )}
                            {!(slot.allowedCategories?.length > 0) &&
                              !(slot.allowedItems?.length > 0) &&
                              slot.slotName?.trim() && (
                              <span
                                className="badge"
                                style={{
                                  backgroundColor: '#fed7d7',
                                  color: '#c53030',
                                  padding: '4px 10px',
                                  borderRadius: '6px',
                                  fontSize: '0.75rem',
                                  fontWeight: '500'
                                }}
                              >
                                <i className="fa fa-exclamation-triangle me-1"></i>
                                Category or item required
                              </span>
                            )}
                            {slot.constraint && (
                              <span 
                                className="badge d-none" 
                                style={{
                                  backgroundColor: '#ed8936',
                                  color: 'white',
                                  padding: '4px 10px',
                                  borderRadius: '6px',
                                  fontSize: '0.75rem',
                                  fontWeight: '500'
                                }}
                              >
                                <i className="fa fa-cog me-1"></i>
                                Constraints Set
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="d-flex gap-2" onClick={(e) => e.stopPropagation()}>
                        {expandedSlots.has(index) && slot.slotID > 0 && (
                          <button
                            type="button"
                            className="deal-slot-btn deal-slot-btn--save"
                            onClick={() => saveSlot(index)}
                            disabled={slotSaving}
                          >
                            <i className="fa fa-save me-1"></i>
                            Save Changes
                          </button>
                        )}
                        {expandedSlots.has(index) && slot.slotID === 0 && (
                          <button
                            type="button"
                            className="deal-slot-btn deal-slot-btn--primary"
                            onClick={() => saveSlot(index)}
                            disabled={slotSaving}
                          >
                            <i className="fa fa-check me-1"></i>
                            Save Slot
                          </button>
                        )}
                        <button
                          type="button"
                          className="deal-slot-btn deal-slot-btn--danger"
                          onClick={() => removeSlotRow(slot, index)}
                          disabled={slotSaving}
                        >
                          <i className="fa fa-trash"></i>
                        </button>
                      </div>
                    </div>
                  </div>

                  {/* Slot Body - Accordion Content */}
                  <div 
                    className="card-body p-4"
                    style={{
                      display: expandedSlots.has(index) ? 'block' : 'none',
                      transition: 'all 0.3s ease',
                      overflow: 'hidden'
                    }}
                  >
                    {/* Basic Information Section */}
                    <div 
                      className="mb-4"
                      style={{
                        padding: '20px',
                        backgroundColor: isDarkMode ? '#2d3436' : '#f8f9fa',
                        borderRadius: '12px',
                        border: '1px solid ' + (isDarkMode ? '#4b5563' : '#e9ecef')
                      }}
                    >
                      <div className="d-flex align-items-center mb-3">
                        <i className="fa fa-info-circle me-2" style={{ color: themeColor }}></i>
                        <h6 className="mb-0" style={{ color: isDarkMode ? '#ffffff' : '#000000', fontWeight: '600' }}>
                          Basic Information
                        </h6>
                      </div>
                      <div className="row g-4">
                        <div className="col-12">
                          <FormInput
                            label="Slot Name *"
                            placeHolder="Enter slot name"
                            type="text"
                            value={slot.slotName || ""}
                            onchange={(e) => handleSlotChange(index, "slotName", e.target.value)}
                          />
                        </div>
                        <div className="col-md-6">
                          <div style={{
                            padding: '16px',
                            backgroundColor: isDarkMode ? '#2d3436' : '#ffffff',
                            borderRadius: '10px',
                            border: `1px solid ${isDarkMode ? '#4a5568' : '#e2e8f0'}`
                          }}>
                            <ToggleSwitch
                              label="Set Required Quantity"
                              isChecked={slot.requiredQty != null && slot.requiredQty !== ""}
                              onToggle={(checked) => {
                                handleSlotChange(index, "requiredQty", checked ? (parseInt(slot.requiredQty, 10) || 0) : null);
                              }}
                              themeColor={themeColor}
                            />
                            {slot.requiredQty != null && slot.requiredQty !== "" && (
                              <div className="mt-3">
                                <FormInput
                                  label="Required Qty"
                                  placeHolder="0"
                                  type="number"
                                  min="0"
                                  value={slot.requiredQty?.toString() ?? "0"}
                                  onchange={(e) => {
                                    const raw = e.target.value === "" ? 0 : parseInt(e.target.value, 10);
                                    const value = isNaN(raw) ? 0 : Math.max(0, raw);
                                    handleSlotChange(index, "requiredQty", value);
                                  }}
                                />
                              </div>
                            )}
                          </div>
                        </div>
                        <div className="col-md-6">
                          <div style={{
                            padding: '16px',
                            backgroundColor: isDarkMode ? '#2d3436' : '#ffffff',
                            borderRadius: '10px',
                            border: `1px solid ${isDarkMode ? '#4a5568' : '#e2e8f0'}`
                          }}>
                            <ToggleSwitch
                              label="Set Variant Name (applies to all items)"
                              isChecked={slot.selectedVariant != null}
                              onToggle={(checked) => {
                                const current = String(slot.selectedVariant || "").trim();
                                handleSlotChange(index, "selectedVariant", checked ? current : null);
                              }}
                              themeColor={themeColor}
                            />
                            {slot.selectedVariant != null && (
                              <div className="mt-3">
                                <FormInput
                                  label="Variant Name"
                                  type="text"
                                  value={slot.selectedVariant || ""}
                                  onchange={(e) => {
                                    handleSlotChange(index, "selectedVariant", e.target.value);
                                  }}
                                  placeHolder="e.g. Large or Large, Medium"
                                />
                                <small className="form-text text-muted mt-1" style={{
                                  display: 'block',
                                  color: isDarkMode ? '#a0aec0' : '#6c757d',
                                  fontSize: '12px',
                                  marginTop: '4px'
                                }}>
                                  One size, or several separated by commas (e.g. Large, Medium). Customers can only pick those variants.
                                </small>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Slot Pricing Configuration - only when Deal Pricing Type is Slot Pricing */}
                    {formState.dealPricingType === "SlotPricing" && (
                    <div 
                      className="mb-4"
                      style={{
                        padding: '20px',
                        backgroundColor: isDarkMode ? '#2d3436' : '#f8f9fa',
                        borderRadius: '12px',
                        border: '1px solid ' + (isDarkMode ? '#4b5563' : '#e9ecef')
                      }}
                    >
                      <div className="d-flex align-items-center mb-3">
                        <i className="fa fa-tag me-2" style={{ color: '#48bb78' }}></i>
                        <h6 className="mb-0" style={{ color: isDarkMode ? '#ffffff' : '#000000', fontWeight: '600' }}>
                          Slot Pricing Configuration
                        </h6>
                      </div>
                      <div className="row g-4">
                          <>
                        <div className="col-md-6">
                          <div style={{
                            padding: '16px',
                            backgroundColor: isDarkMode ? '#2d3436' : '#ffffff',
                            borderRadius: '10px',
                            border: `1px solid ${isDarkMode ? '#4a5568' : '#e2e8f0'}`
                          }}>
                            <label className="form-label fw-semibold mb-2" style={{ color: isDarkMode ? '#ffffff' : '#2d3748' }}>
                              Pricing Type
                            </label>
                            <Select
                              options={[
                                { value: "NoDiscount", label: "No discount on this slot" },
                                { value: "Free", label: "Free" },
                                { value: "Discount", label: "Custom Discount %" },
                                { value: "FixedPricePerItem", label: "Fixed Price (Per Item)" },
                                { value: "FixedPricePerSlot", label: "Fixed Price (Per Slot)" }
                              ]}
                              value={(() => {
                                const pt = slot.pricingType || "NoDiscount";
                                const labels = {
                                  NoDiscount: "No discount on this slot",
                                  Fixed: "No discount on this slot",
                                  Free: "Free",
                                  Discount: "Custom Discount %",
                                  FixedPricePerItem: "Fixed Price (Per Item)",
                                  CustomPrice: "Fixed Price (Per Item)",
                                  FixedPricePerSlot: "Fixed Price (Per Slot)"
                                };
                                const value = pt === "Fixed" ? "NoDiscount" : pt === "CustomPrice" ? "FixedPricePerItem" : pt;
                                return { value, label: labels[pt] || labels.NoDiscount };
                              })()}
                              onChange={(option) => {
                                const newPricingType = option ? option.value : "NoDiscount";
                                handleSlotChange(index, "pricingType", newPricingType);
                                if (newPricingType === "Free") {
                                  handleSlotChange(index, "isFree", true);
                                  handleSlotChange(index, "discountPercentage", null);
                                  handleSlotChange(index, "customPrice", null);
                                } else if (newPricingType === "NoDiscount" || newPricingType === "FixedPricePerItem" || newPricingType === "FixedPricePerSlot") {
                                  handleSlotChange(index, "isFree", false);
                                  handleSlotChange(index, "discountPercentage", null);
                                  if (newPricingType === "NoDiscount") handleSlotChange(index, "customPrice", null);
                                } else if (newPricingType === "Discount") {
                                  handleSlotChange(index, "isFree", false);
                                  handleSlotChange(index, "customPrice", null);
                                }
                              }}
                              styles={dropdownStyles}
                              placeholder="Select pricing type"
                              menuPortalTarget={document.body}
                              menuPosition="fixed"
                              menuShouldBlockScroll={false}
                              menuShouldScrollIntoView={false}
                              closeMenuOnScroll={false}
                            />
                            {(slot.pricingType === "Discount") && (
                              <div className="mt-3">
                                <FormInput
                                  label="Discount Percentage"
                                  type="number"
                                  min="0"
                                  max="100"
                                  value={slot.discountPercentage?.toString() ?? ""}
                                  onchange={(e) => handleSlotChange(index, "discountPercentage", e.target.value ? parseFloat(e.target.value) : null)}
                                  placeHolder="0-100"
                                />
                              </div>
                            )}
                            {(slot.pricingType === "FixedPricePerItem" || slot.pricingType === "CustomPrice") && (
                              <div className="mt-3">
                                <FormInput
                                  label="Fixed Price (Per Item) *"
                                  type="number"
                                  min="0"
                                  step="0.01"
                                  value={slot.customPrice?.toString() ?? ""}
                                  onchange={(e) => handleSlotChange(index, "customPrice", e.target.value && e.target.value.trim() !== "" ? parseFloat(e.target.value) : null)}
                                  placeHolder="0.00"
                                />
                                <small className="text-muted" style={{ color: isDarkMode ? '#a0aec0' : '#718096', fontSize: '12px', display: 'block', marginTop: '4px' }}>
                                  Fixed price per item (e.g., 5.00 for £5 each)
                                </small>
                              </div>
                            )}
                            {slot.pricingType === "FixedPricePerSlot" && (
                              <div className="mt-3">
                                <FormInput
                                  label="Fixed Price (Per Slot) *"
                                  type="number"
                                  min="0"
                                  step="0.01"
                                  value={slot.customPrice?.toString() ?? ""}
                                  onchange={(e) => handleSlotChange(index, "customPrice", e.target.value && e.target.value.trim() !== "" ? parseFloat(e.target.value) : null)}
                                  placeHolder="0.00"
                                />
                                <small className="text-muted" style={{ color: isDarkMode ? '#a0aec0' : '#718096', fontSize: '12px', display: 'block', marginTop: '4px' }}>
                                  Fixed price for the whole slot (e.g., 10.00 for £10 per slot)
                                </small>
                              </div>
                            )}
                            {slot.pricingType === "Free" && (
                              <div className="mt-3">
                                <ToggleSwitch
                                  label="Auto-duplicate item for BOGO"
                                  isChecked={slot.autoDuplicateItem ?? false}
                                  onToggle={(checked) => handleSlotChange(index, "autoDuplicateItem", checked)}
                                  themeColor={themeColor}
                                />
                                <small className="text-muted" style={{ color: isDarkMode ? '#a0aec0' : '#718096', fontSize: '12px', display: 'block', marginTop: '8px' }}>
                                  When enabled, adding one item will automatically add the same item with the same modifiers
                                </small>
                              </div>
                            )}
                          </div>
                        </div>
                        {slot.pricingType !== "NoDiscount" && slot.pricingType !== "Fixed" && slot.pricingType != null && (
                        <div className="col-md-6">
                          <div style={{
                            padding: '16px',
                            backgroundColor: isDarkMode ? '#2d3436' : '#ffffff',
                            borderRadius: '10px',
                            border: `1px solid ${isDarkMode ? '#4a5568' : '#e2e8f0'}`
                          }}>
                            <ToggleSwitch
                              label="Set items at full price (rest get discount)"
                              isChecked={
                                slot.buyQuantity === "" ||
                                (slot.buyQuantity != null && Number(slot.buyQuantity) > 0)
                              }
                              onToggle={(checked) => {
                                if (!checked) {
                                  handleSlotChange(index, "buyQuantity", null);
                                  return;
                                }
                                // Keep an existing positive value; otherwise leave the field empty.
                                const existing = parseInt(slot.buyQuantity, 10);
                                handleSlotChange(
                                  index,
                                  "buyQuantity",
                                  Number.isFinite(existing) && existing > 0 ? existing : ""
                                );
                              }}
                              themeColor={themeColor}
                            />
                            {(slot.buyQuantity === "" ||
                              (slot.buyQuantity != null && Number(slot.buyQuantity) > 0)) && (
                              <div className="mt-3">
                                <FormInput
                                  label="Items at full price (rest get discount)"
                                  type="number"
                                  min="1"
                                  value={
                                    slot.buyQuantity === "" || slot.buyQuantity == null
                                      ? ""
                                      : String(slot.buyQuantity)
                                  }
                                  onchange={(e) => {
                                    const raw = e.target.value;
                                    // Empty keeps the toggle on; do not clear it to null.
                                    if (raw === "") {
                                      handleSlotChange(index, "buyQuantity", "");
                                      return;
                                    }
                                    const parsed = parseInt(raw, 10);
                                    if (isNaN(parsed) || parsed < 1) {
                                      handleSlotChange(index, "buyQuantity", "");
                                      return;
                                    }
                                    handleSlotChange(index, "buyQuantity", parsed);
                                  }}
                                  placeHolder="e.g. 1"
                                />
                                <small className="text-muted" style={{ color: isDarkMode ? '#a0aec0' : '#718096', fontSize: '12px', display: 'block', marginTop: '4px' }}>
                                  Out of the required quantity: this many at full price; the rest get the discount. E.g. Required 2, value 1 = 1 full price, 1 discount (BOGO).
                                </small>
                              </div>
                            )}
                          </div>
                        </div>
                        )}
                          </>
                      </div>
                    </div>
                    )}

                    {/* Constraints Section - hidden */}
                    <div 
                      className="mb-4 d-none"
                      style={{
                        padding: '20px',
                        backgroundColor: isDarkMode ? '#2d3436' : '#f8f9fa',
                        borderRadius: '12px',
                        border: '1px solid ' + (isDarkMode ? '#4b5563' : '#e9ecef')
                      }}
                    >
                      <div className="d-flex align-items-center mb-3">
                        <i className="fa fa-cog me-2" style={{ color: '#ed8936' }}></i>
                        <h6 className="mb-0" style={{ color: isDarkMode ? '#ffffff' : '#000000', fontWeight: '600' }}>
                          Constraints
                        </h6>
                      </div>
                      <div className="row g-3">
                        <div className="col-md-6 d-none">
                          <FormInput
                            label="Min Qty (Optional)"
                            placeHolder="Leave empty for no limit"
                            type="number"
                            min="1"
                            value={slot.constraint?.minQty?.toString() || ""}
                            onchange={(e) => handleConstraintChange(index, "minQty", e.target.value ? parseInt(e.target.value) : null)}
                          />
                        </div>
                        <div className="col-md-6 d-none">
                          <FormInput
                            label="Max Qty (Optional)"
                            placeHolder="Leave empty for no limit"
                            type="number"
                            min="1"
                            value={slot.constraint?.maxQty?.toString() || ""}
                            onchange={(e) => handleConstraintChange(index, "maxQty", e.target.value ? parseInt(e.target.value) : null)}
                          />
                        </div>
                        <div className="col-md-6">
                          <div style={{
                            padding: '12px',
                            backgroundColor: isDarkMode ? '#2d3436' : '#ffffff',
                            borderRadius: '8px',
                            border: `1px solid ${isDarkMode ? '#4a5568' : '#e2e8f0'}`
                          }}>
                            <ToggleSwitch
                              label="Allow Duplicate Items"
                              isChecked={slot.constraint?.allowDuplicateItems !== false}
                              onToggle={(checked) => handleConstraintChange(index, "allowDuplicateItems", checked)}
                              themeColor={themeColor}
                            />
                          </div>
                        </div>
                        <div className="col-md-6">
                          <div style={{
                            padding: '12px',
                            backgroundColor: isDarkMode ? '#2d3436' : '#ffffff',
                            borderRadius: '8px',
                            border: `1px solid ${isDarkMode ? '#4a5568' : '#e2e8f0'}`
                          }}>
                            <ToggleSwitch
                              label="Allow Mixed Categories"
                              isChecked={slot.constraint?.allowMixedCategories !== false}
                              onToggle={(checked) => handleConstraintChange(index, "allowMixedCategories", checked)}
                              themeColor={themeColor}
                            />
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Categories/Items Selection Section */}
                    <div 
                      className="mb-4"
                      style={{
                        padding: '20px',
                        backgroundColor: isDarkMode ? '#2d3436' : '#f8f9fa',
                        borderRadius: '12px',
                        border: '1px solid ' + (isDarkMode ? '#4b5563' : '#e9ecef')
                      }}
                    >
                      {/* Categories Section */}
                      {slot.allowedCategories && slot.allowedCategories.length > 0 ? (
                        <div>
                          <div className="d-flex align-items-center mb-3">
                            <h6 
                              className="mb-0" 
                              style={{ 
                                color: isDarkMode ? '#ffffff' : '#000000', 
                                fontWeight: '600',
                                fontSize: '16px'
                              }}
                            >
                              Selected Categories *
                            </h6>
                          </div>
                          <div style={{
                            padding: '16px',
                            backgroundColor: isDarkMode ? '#2d3436' : '#ffffff',
                            borderRadius: '10px',
                            border: `1px solid ${isDarkMode ? '#4a5568' : '#e2e8f0'}`,
                            minHeight: '80px'
                          }}>
                            {slot.allowedCategories.length > 0 ? (
                              <>
                                <div className="row row-cols-1 row-cols-md-2 row-cols-lg-3 g-3">
                                  {slot.allowedCategories.filter((cat, catIdx, list) => {
                                    const catID = String(cat.categoryID || cat.CategoryID || "");
                                    if (!catID) return Boolean(cat);
                                    return list.findIndex((c) => String(c.categoryID || c.CategoryID || "") === catID) === catIdx;
                                  }).map((cat, catIdx) => {
                                    const catID = cat.categoryID || cat.CategoryID;
                                    const category = findCategoryById(catID);
                                    if (!category && !catID) return null;
                                    const categoryName =
                                      getCategoryDisplayName(category) ||
                                      getCategoryDisplayName(cat) ||
                                      getCategoryName(catID);
                                    return (
                                      <div key={cat.id || cat.ID || catIdx} className="col">
                                        <div 
                                          className="category-chip"
                                          style={{
                                            background: isDarkMode ? '#404040' : '#ffffff',
                                            borderRadius: '12px',
                                            padding: '16px 20px',
                                            border: isDarkMode ? '2px solid #555' : '2px solid #e9ecef',
                                            transition: 'all 0.2s ease',
                                            cursor: 'default',
                                            position: 'relative',
                                            boxShadow: isDarkMode ? '0 2px 8px rgba(0, 0, 0, 0.3)' : '0 2px 8px rgba(0, 0, 0, 0.05)'
                                          }}
                                          onMouseEnter={(e) => {
                                            e.currentTarget.style.borderColor = '#007bff';
                                            e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 123, 255, 0.15)';
                                            e.currentTarget.style.transform = 'translateY(-2px)';
                                          }}
                                          onMouseLeave={(e) => {
                                            e.currentTarget.style.borderColor = isDarkMode ? '#555' : '#e9ecef';
                                            e.currentTarget.style.boxShadow = isDarkMode ? '0 2px 8px rgba(0, 0, 0, 0.3)' : '0 2px 8px rgba(0, 0, 0, 0.05)';
                                            e.currentTarget.style.transform = 'translateY(0)';
                                          }}
                                        >
                                          <div className="d-flex justify-content-between align-items-center">
                                            <div className="d-flex align-items-center">
                                              {/* Drag Handle */}
                                              <div 
                                                style={{
                                                  width: '20px',
                                                  height: '20px',
                                                  display: 'flex',
                                                  alignItems: 'center',
                                                  justifyContent: 'center',
                                                  marginRight: '8px',
                                                  cursor: 'grab',
                                                  color: isDarkMode ? '#ffffff' : '#6c757d',
                                                  borderRadius: '4px',
                                                  transition: 'all 0.2s ease'
                                                }}
                                                onMouseEnter={(e) => {
                                                  e.currentTarget.style.background = isDarkMode ? '#555' : '#f8f9fa';
                                                  e.currentTarget.style.color = isDarkMode ? '#ffffff' : '#495057';
                                                }}
                                                onMouseLeave={(e) => {
                                                  e.currentTarget.style.background = 'transparent';
                                                  e.currentTarget.style.color = isDarkMode ? '#ffffff' : '#6c757d';
                                                }}
                                              >
                                                <i className="fa fa-bars" style={{ fontSize: '12px' }}></i>
                                              </div>
                                              
                                              {/* Category Icon */}
                                              <div 
                                                style={{
                                                  width: '36px',
                                                  height: '36px',
                                                  background: isDarkMode ? '#555' : '#f8f9fa',
                                                  borderRadius: '8px',
                                                  display: 'flex',
                                                  alignItems: 'center',
                                                  justifyContent: 'center',
                                                  marginRight: '12px',
                                                  border: isDarkMode ? '1px solid #666' : '1px solid #e9ecef'
                                                }}
                                              >
                                                <i className="fa fa-tag" style={{ fontSize: '14px', color: isDarkMode ? '#ffffff' : '#6c757d' }}></i>
                                              </div>
                                              
                                              {/* Category Name */}
                                              <div>
                                                <span 
                                                  style={{ 
                                                    fontSize: '15px', 
                                                    fontWeight: '500',
                                                    color: isDarkMode ? '#ffffff' : '#495057'
                                                  }}
                                                >
                                                  {categoryName}
                                                </span>
                                              </div>
                                            </div>
                                            
                                            {/* Remove Button */}
                                            <button
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                handleDeleteCategory(catID, slot.slotID);
                                              }}
                                              style={{
                                                background: 'transparent',
                                                border: 'none',
                                                borderRadius: '6px',
                                                width: '28px',
                                                height: '28px',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                cursor: 'pointer',
                                                transition: 'all 0.2s ease',
                                                color: '#dc3545'
                                              }}
                                              onMouseEnter={(e) => {
                                                e.currentTarget.style.background = isDarkMode ? '#4a1a1a' : '#f8d7da';
                                                e.currentTarget.style.color = isDarkMode ? '#ff6b6b' : '#721c24';
                                              }}
                                              onMouseLeave={(e) => {
                                                e.currentTarget.style.background = 'transparent';
                                                e.currentTarget.style.color = '#dc3545';
                                              }}
                                            >
                                              <i 
                                                className="fa fa-times" 
                                                style={{ 
                                                  fontSize: '12px'
                                                }}
                                              ></i>
                                            </button>
                                          </div>
                                        </div>
                                      </div>
                                    );
                                  })}
                                </div>
                                <div className="d-flex justify-content-center mt-4">
                                  <button
                                    onClick={() => handleManageCategories(slot, index)}
                                    style={{
                                      background: themeColor,
                                      border: 'none',
                                      borderRadius: '8px',
                                      padding: '10px 20px',
                                      color: 'white',
                                      fontSize: '14px',
                                      fontWeight: '500',
                                      cursor: 'pointer',
                                      transition: 'all 0.2s ease',
                                      display: 'flex',
                                      alignItems: 'center',
                                      gap: '8px'
                                    }}
                                    onMouseEnter={(e) => {
                                      e.currentTarget.style.background = themeColor + 'dd';
                                      e.currentTarget.style.transform = 'translateY(-1px)';
                                    }}
                                    onMouseLeave={(e) => {
                                      e.currentTarget.style.background = themeColor;
                                      e.currentTarget.style.transform = 'translateY(0)';
                                    }}
                                  >
                                    <i className="fa-solid fa-plus" style={{ fontSize: '12px' }}></i>
                                    Add More Category
                                  </button>
                                </div>
                              </>
                            ) : (
                              <div className="text-center py-4">
                                <div 
                                  style={{
                                    width: '60px',
                                    height: '60px',
                                    background: isDarkMode ? '#555' : '#f8f9fa',
                                    borderRadius: '12px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    margin: '0 auto 16px',
                                    border: isDarkMode ? '1px solid #666' : '1px solid #e9ecef'
                                  }}
                                >
                                  <i className="fa fa-tags" style={{ fontSize: '24px', color: isDarkMode ? '#ffffff' : '#6c757d' }}></i>
                                </div>
                                <p style={{ color: isDarkMode ? '#ffffff' : '#6c757d', fontSize: '15px', marginBottom: '20px' }}>No Category selected</p>
                                <button
                                  onClick={() => handleManageCategories(slot, index)}
                                  style={{
                                    background: themeColor,
                                    border: 'none',
                                    borderRadius: '8px',
                                    padding: '10px 20px',
                                    color: 'white',
                                    fontSize: '14px',
                                    fontWeight: '500',
                                    cursor: 'pointer',
                                    transition: 'all 0.2s ease',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '8px',
                                    margin: '0 auto'
                                  }}
                                  onMouseEnter={(e) => {
                                    e.currentTarget.style.background = themeColor + 'dd';
                                    e.currentTarget.style.transform = 'translateY(-1px)';
                                  }}
                                  onMouseLeave={(e) => {
                                    e.currentTarget.style.background = themeColor;
                                    e.currentTarget.style.transform = 'translateY(0)';
                                  }}
                                >
                                  <i className="fa-solid fa-plus" style={{ fontSize: '12px' }}></i>
                                  Add Category
                                </button>
                              </div>
                            )}
                          </div>
                        </div>
                      ) : slot.allowedItems && slot.allowedItems.length > 0 ? (
                        <div>
                          <div className="d-flex align-items-center mb-3">
                            <h6 
                              className="mb-0" 
                              style={{ 
                                color: isDarkMode ? '#ffffff' : '#000000', 
                                fontWeight: '600',
                                fontSize: '16px'
                              }}
                            >
                              Selected Items *
                            </h6>
                          </div>
                          <div style={{
                            padding: '16px',
                            backgroundColor: isDarkMode ? '#2d3436' : '#ffffff',
                            borderRadius: '10px',
                            border: `1px solid ${isDarkMode ? '#4a5568' : '#e2e8f0'}`,
                            minHeight: '80px'
                          }}>
                            {slot.allowedItems.length > 0 ? (
                              <>
                                <div className="row row-cols-1 row-cols-md-2 row-cols-lg-3 g-3">
                                  {slot.allowedItems.map((item, itemIdx) => {
                                    const itemIDRaw =
                                      typeof item === "object"
                                        ? (item.itemID || item.ItemID || item.menuItem_ID || item.MenuItem_ID || item.id)
                                        : item;
                                    const itemID = itemIDRaw !== undefined && itemIDRaw !== null ? parseInt(itemIDRaw, 10) : null;
                                    const menuItem = allItems.find(i => parseInt(i.menuItem_ID || i.MenuItem_ID || i.id, 10) === itemID);
                                    if (!menuItem && !itemID) return null;
                                    const itemName = menuItem?.menuItem_Name || menuItem?.MenuItem_Name || menuItem?.name || `Item ${itemID}`;
                                    return (
                                      <div key={item.id || item.ID || itemIdx} className="col">
                                        <div 
                                          className="item-chip"
                                          style={{
                                            background: isDarkMode ? '#404040' : '#ffffff',
                                            borderRadius: '12px',
                                            padding: '16px 20px',
                                            border: isDarkMode ? '2px solid #555' : '2px solid #e9ecef',
                                            transition: 'all 0.2s ease',
                                            cursor: 'default',
                                            position: 'relative',
                                            boxShadow: isDarkMode ? '0 2px 8px rgba(0, 0, 0, 0.3)' : '0 2px 8px rgba(0, 0, 0, 0.05)'
                                          }}
                                          onMouseEnter={(e) => {
                                            e.currentTarget.style.borderColor = '#007bff';
                                            e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 123, 255, 0.15)';
                                            e.currentTarget.style.transform = 'translateY(-2px)';
                                          }}
                                          onMouseLeave={(e) => {
                                            e.currentTarget.style.borderColor = isDarkMode ? '#555' : '#e9ecef';
                                            e.currentTarget.style.boxShadow = isDarkMode ? '0 2px 8px rgba(0, 0, 0, 0.3)' : '0 2px 8px rgba(0, 0, 0, 0.05)';
                                            e.currentTarget.style.transform = 'translateY(0)';
                                          }}
                                        >
                                          <div className="d-flex justify-content-between align-items-center">
                                            <div className="d-flex align-items-center">
                                              {/* Drag Handle */}
                                              <div 
                                                style={{
                                                  width: '20px',
                                                  height: '20px',
                                                  display: 'flex',
                                                  alignItems: 'center',
                                                  justifyContent: 'center',
                                                  marginRight: '8px',
                                                  cursor: 'grab',
                                                  color: isDarkMode ? '#ffffff' : '#6c757d',
                                                  borderRadius: '4px',
                                                  transition: 'all 0.2s ease'
                                                }}
                                                onMouseEnter={(e) => {
                                                  e.currentTarget.style.background = isDarkMode ? '#555' : '#f8f9fa';
                                                  e.currentTarget.style.color = isDarkMode ? '#ffffff' : '#495057';
                                                }}
                                                onMouseLeave={(e) => {
                                                  e.currentTarget.style.background = 'transparent';
                                                  e.currentTarget.style.color = isDarkMode ? '#ffffff' : '#6c757d';
                                                }}
                                              >
                                                <i className="fa fa-bars" style={{ fontSize: '12px' }}></i>
                                              </div>
                                              
                                              {/* Item Icon */}
                                              <div 
                                                style={{
                                                  width: '36px',
                                                  height: '36px',
                                                  background: isDarkMode ? '#555' : '#f8f9fa',
                                                  borderRadius: '8px',
                                                  display: 'flex',
                                                  alignItems: 'center',
                                                  justifyContent: 'center',
                                                  marginRight: '12px',
                                                  border: isDarkMode ? '1px solid #666' : '1px solid #e9ecef'
                                                }}
                                              >
                                                <i className="fa fa-box" style={{ fontSize: '14px', color: isDarkMode ? '#ffffff' : '#6c757d' }}></i>
                                              </div>
                                              
                                              {/* Item Name */}
                                              <div>
                                                <span 
                                                  style={{ 
                                                    fontSize: '15px', 
                                                    fontWeight: '500',
                                                    color: isDarkMode ? '#ffffff' : '#495057'
                                                  }}
                                                >
                                                  {itemName}
                                                </span>
                                              </div>
                                            </div>
                                            
                                            {/* Remove Button */}
                                            <button
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                handleDeleteItem(itemID, slot.slotID);
                                              }}
                                              style={{
                                                background: 'transparent',
                                                border: 'none',
                                                borderRadius: '6px',
                                                width: '28px',
                                                height: '28px',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                cursor: 'pointer',
                                                transition: 'all 0.2s ease',
                                                color: '#dc3545'
                                              }}
                                              onMouseEnter={(e) => {
                                                e.currentTarget.style.background = isDarkMode ? '#4a1a1a' : '#f8d7da';
                                                e.currentTarget.style.color = isDarkMode ? '#ff6b6b' : '#721c24';
                                              }}
                                              onMouseLeave={(e) => {
                                                e.currentTarget.style.background = 'transparent';
                                                e.currentTarget.style.color = '#dc3545';
                                              }}
                                            >
                                              <i 
                                                className="fa fa-times" 
                                                style={{ 
                                                  fontSize: '12px'
                                                }}
                                              ></i>
                                            </button>
                                          </div>
                                        </div>
                                      </div>
                                    );
                                  })}
                                </div>
                                <div className="d-flex justify-content-center mt-4">
                                  <button
                                    onClick={() => handleManageItems(slot, index)}
                                    style={{
                                      background: themeColor,
                                      border: 'none',
                                      borderRadius: '8px',
                                      padding: '10px 20px',
                                      color: 'white',
                                      fontSize: '14px',
                                      fontWeight: '500',
                                      cursor: 'pointer',
                                      transition: 'all 0.2s ease',
                                      display: 'flex',
                                      alignItems: 'center',
                                      gap: '8px'
                                    }}
                                    onMouseEnter={(e) => {
                                      e.currentTarget.style.background = themeColor + 'dd';
                                      e.currentTarget.style.transform = 'translateY(-1px)';
                                    }}
                                    onMouseLeave={(e) => {
                                      e.currentTarget.style.background = themeColor;
                                      e.currentTarget.style.transform = 'translateY(0)';
                                    }}
                                  >
                                    <i className="fa-solid fa-plus" style={{ fontSize: '12px' }}></i>
                                    Add More Item
                                  </button>
                                </div>
                              </>
                            ) : (
                              <div className="text-center py-4">
                                <div 
                                  style={{
                                    width: '60px',
                                    height: '60px',
                                    background: isDarkMode ? '#555' : '#f8f9fa',
                                    borderRadius: '12px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    margin: '0 auto 16px',
                                    border: isDarkMode ? '1px solid #666' : '1px solid #e9ecef'
                                  }}
                                >
                                  <i className="fa fa-box" style={{ fontSize: '24px', color: isDarkMode ? '#ffffff' : '#6c757d' }}></i>
                                </div>
                                <p style={{ color: isDarkMode ? '#ffffff' : '#6c757d', fontSize: '15px', marginBottom: '20px' }}>No Item selected</p>
                                <button
                                  onClick={() => handleManageItems(slot, index)}
                                  style={{
                                    background: themeColor,
                                    border: 'none',
                                    borderRadius: '8px',
                                    padding: '10px 20px',
                                    color: 'white',
                                    fontSize: '14px',
                                    fontWeight: '500',
                                    cursor: 'pointer',
                                    transition: 'all 0.2s ease',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '8px',
                                    margin: '0 auto'
                                  }}
                                  onMouseEnter={(e) => {
                                    e.currentTarget.style.background = themeColor + 'dd';
                                    e.currentTarget.style.transform = 'translateY(-1px)';
                                  }}
                                  onMouseLeave={(e) => {
                                    e.currentTarget.style.background = themeColor;
                                    e.currentTarget.style.transform = 'translateY(0)';
                                  }}
                                >
                                  <i className="fa-solid fa-plus" style={{ fontSize: '12px' }}></i>
                                  Add Item
                                </button>
                              </div>
                            )}
                          </div>
                        </div>
                      ) : (
                        <div>
                          <div className="d-flex align-items-center mb-3">
                            <h6 
                              className="mb-0" 
                              style={{ 
                                color: isDarkMode ? '#ffffff' : '#000000', 
                                fontWeight: '600',
                                fontSize: '16px'
                              }}
                            >
                              Select Categories or Items
                            </h6>
                          </div>
                          <div className="text-center py-4">
                            <div 
                              style={{
                                width: '60px',
                                height: '60px',
                                background: isDarkMode ? '#555' : '#f8f9fa',
                                borderRadius: '12px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                margin: '0 auto 16px',
                                border: isDarkMode ? '1px solid #666' : '1px solid #e9ecef'
                              }}
                            >
                              <i className="fa fa-info-circle" style={{ fontSize: '24px', color: isDarkMode ? '#ffffff' : '#6c757d' }}></i>
                            </div>
                            <p style={{ color: isDarkMode ? '#ffffff' : '#6c757d', fontSize: '15px', marginBottom: '20px' }}>
                              No selection type chosen. Please select Categories or Items.
                            </p>
                            <div className="d-flex gap-2 justify-content-center">
                              <button
                                onClick={() => {
                                  selectedSlotRef.current = slot;
                                  selectedSlotIndexRef.current = index;
                                  setSelectedSlot(slot);
                                  handleTypeSelection('categories');
                                }}
                                style={{
                                  background: '#4299e1',
                                  border: 'none',
                                  borderRadius: '8px',
                                  padding: '10px 20px',
                                  color: 'white',
                                  fontSize: '14px',
                                  fontWeight: '500',
                                  cursor: 'pointer',
                                  transition: 'all 0.2s ease',
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '8px'
                                }}
                                onMouseEnter={(e) => {
                                  e.currentTarget.style.background = '#3182ce';
                                  e.currentTarget.style.transform = 'translateY(-1px)';
                                }}
                                onMouseLeave={(e) => {
                                  e.currentTarget.style.background = '#4299e1';
                                  e.currentTarget.style.transform = 'translateY(0)';
                                }}
                              >
                                <i className="fa fa-tags me-1"></i>
                                Select Categories
                              </button>
                              <button
                                onClick={() => {
                                  selectedSlotRef.current = slot;
                                  selectedSlotIndexRef.current = index;
                                  setSelectedSlot(slot);
                                  handleTypeSelection('items');
                                }}
                                style={{
                                  background: '#48bb78',
                                  border: 'none',
                                  borderRadius: '8px',
                                  padding: '10px 20px',
                                  color: 'white',
                                  fontSize: '14px',
                                  fontWeight: '500',
                                  cursor: 'pointer',
                                  transition: 'all 0.2s ease',
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '8px'
                                }}
                                onMouseEnter={(e) => {
                                  e.currentTarget.style.background = '#38a169';
                                  e.currentTarget.style.transform = 'translateY(-1px)';
                                }}
                                onMouseLeave={(e) => {
                                  e.currentTarget.style.background = '#48bb78';
                                  e.currentTarget.style.transform = 'translateY(0)';
                                }}
                              >
                                <i className="fa fa-box me-1"></i>
                                Select Items
                              </button>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
                );
              })}
            </div>
          )}
        </DealSection>
      </div>

          </>
        )}
      </GridSectionLoader>

      {/* Type Selection Modal */}
      <DealSlotTypeSelectionModal
        isOpen={showTypeSelectionModal}
        onClose={() => {
          setShowTypeSelectionModal(false);
          // Don't clear slot if we're transitioning to category/item modal
          // Check if type selection is in progress (handleTypeSelection was just called)
          // or if modals are already open
          if (!typeSelectionInProgressRef.current) {
            // User closed without selecting - clear the slot
            // But wait a bit to ensure handleTypeSelection had time to run if it was called
            setTimeout(() => {
              // Double check - if type selection didn't happen and modals didn't open, clear
              if (!typeSelectionInProgressRef.current && !showCategoryModal && !showItemModal) {
                setSelectedSlot(null);
                selectedSlotRef.current = null;
                selectedSlotIndexRef.current = null;
                setSelectedType(null);
              }
            }, 150);
          }
          // If typeSelectionInProgressRef is true, handleTypeSelection was called
          // and will open the modal - don't clear the slot
        }}
        onSelectType={handleTypeSelection}
      />

      {/* Category Selection Modal */}
      <CategorySelector
        selectedCategories={selectedCategories}
        setSelectedCategories={setSelectedCategories}
        showModal={showCategoryModal}
        setShowModal={(val) => {
          setShowCategoryModal(val);
          // Only clear selectedSlot if modal is closed without saving (e.g., clicking X)
          // Don't clear here when "Done" is clicked - handleSaveCategories will handle it
          if (!val) {
            // Reset type selection flag when modal closes
            typeSelectionInProgressRef.current = false;
            // Wait a bit to see if save handler clears it
            setTimeout(() => {
              // Only clear if still not null (meaning save didn't run or didn't clear it)
              if (selectedSlotRef.current || selectedSlot) {
                selectedSlotRef.current = null;
                setSelectedSlot(null);
                setSelectedType(null);
                selectedSlotIndexRef.current = null;
              }
            }, 2000);
          }
        }}
        onModalSubmit={handleSaveCategories}
        setLoading={setCategoryModalLoading}
        hideDisplay={true}
      />

      {/* Item Selection Modal */}
      <ProductMultiSelectionModal
        showModal={showItemModal}
        setShowModal={(value) => {
          setShowItemModal(value);
          // Only clear selectedSlot if modal is closed without saving (e.g., clicking X)
          // Don't clear here when "Done" is clicked - handleSaveItems will handle it
          if (!value) {
            // Reset type selection flag when modal closes
            typeSelectionInProgressRef.current = false;
            // Wait a bit to see if save handler clears it
            setTimeout(() => {
              // Only clear if still not null (meaning save didn't run or didn't clear it)
              if (selectedSlotRef.current || selectedSlot) {
                selectedSlotRef.current = null;
                setSelectedSlot(null);
                setSelectedType(null);
                selectedSlotIndexRef.current = null;
              }
            }, 2000);
          }
        }}
        showButton={true}
        onSubmit={handleSaveItems}
        setSelectedProducts={setSelectedItems}
        selectedProducts={selectedItems}
        products={allItems}
      />

    </div>
  );
};

export default UpdateDeal;
